import {
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SafeResourceUrlPipe } from '../../../shared/pipes/safe-resource-url.pipe';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { SearchSelectComponent } from '../../../shared/components/search-select/search-select.component';
import { VendorService } from '../../vendors/vendor.service';
import {
  PoService,
  VendorSkuMappingItem,
  VendorSkuMappingMode,
  VendorSkuMappingPreview,
} from '../po.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  Observable,
  firstValueFrom,
} from 'rxjs';
import { ProductsService } from '../../products/products.service';
import { withMatchedEan } from '../../../core/utils/current-ean.util';
import {
  hasPurchasePrice,
  priceNotSetMessage,
} from '../../../core/utils/price-status.util';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { environment } from '../../../../environments/environment';
import { IndentListService } from '../../indent-list/indent-list.service';
import {
  WarehouseSelectComponent,
  WarehouseOption,
} from '../../../shared/components/warehouse-select/warehouse-select.component';
import { QtyInputDirective } from '../../../shared/directives/qty-input.directive';
import { StickyColsDirective } from '../../../shared/directives/sticky-cols.directive';
import { consistentBasicCost, pricingForCost, pricingForLine } from '../../../shared/utils/basic-cost.util';
import { allowsDecimalQty } from '../../../shared/utils/quantity.util';
import { ScanTypeahead } from '../../../shared/utils/scan-typeahead.util';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';
import { AuthService } from '../../../auth/auth.service';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';

/** The confirm dialog renders its message as HTML. */
function escapeHtml(text: string): string {
  return String(text).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** What the browser safety copy of an unsaved PO holds (see autosaveDraft). */
interface PoDraftSnapshot {
  selectedVendor: string | null;
  /** The vendor row itself, so the picker can label it before the list loads. */
  vendor: any;
  deliveryDate: string;
  expiryDate: string;
  packingCharges: number;
  schemeDiscountAmount: number;
  fixedDiscountAmount: number;
  costPriceIncludesGst: boolean;
  fromDate: string;
  selectedIndents: string[];
  /** Labels of the selected indents only. */
  indentOptions: { id: string; label: string }[];
  selectedManufacturers: string[];
  selectedBrands: string[];
  selectedDepartment: any;
  selectedCategory: string | null;
  selectedSubCategories: string[];
  poItems: any[];
}

/** What the buyer chose for one unmapped SKU in the mapping dialog. */
export type VendorSkuMappingDecision = 'map' | 'skip' | 'remove';

interface VendorSkuMappingDialogRow extends VendorSkuMappingItem {
  decision: VendorSkuMappingDecision;
}

interface VendorSkuMappingDialogResult {
  /** Product ids to map to the vendor. */
  map: string[];
  /** Product ids to post without a mapping (PROMPT mode only). */
  skip: string[];
  /** Lines (productVariantIds) the buyer chose to drop from the order. */
  removeVariantIds: string[];
}

interface VendorSkuMappingDialogState {
  /** submit: decisions ride on the save; review: "Map now" ahead of submit. */
  purpose: 'submit' | 'review';
  mode: VendorSkuMappingMode;
  vendorName: string;
  rows: VendorSkuMappingDialogRow[];
  /** Lines on the order (mapped + unmapped) — for the "n of m" headline. */
  total: number;
  busy: boolean;
  resolve: (result: VendorSkuMappingDialogResult | null) => void;
}

@Component({
  selector: 'app-create-po',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgSelectComponent,
    NgSelectModule,
    SearchSelectComponent,
    SafeResourceUrlPipe,
    WarehouseSelectComponent,
    QtyInputDirective,
    StickyColsDirective,
    RouterLink,
  ],
  templateUrl: './create-po.component.html',
  styleUrl: './create-po.component.scss',
})
export class CreatePoComponent implements OnInit, OnDestroy {
  /**
   * Whether this line may be keyed in fractions - bulk, weighable, bulk-child
   * or a kg/gm/ltr unit. Counted SKUs (PCS, NOS, PKT ...) stay whole. The
   * server applies the same rule on save.
   */
  allowsDecimal(source: any): boolean {
    return allowsDecimalQty(source);
  }

  @ViewChild('quantityInput') quantityInputRef!: ElementRef;
  @ViewChild('skuScanSelect') skuScanSelect?: NgSelectComponent;
  @ViewChild('eanScanSelect') eanScanSelect?: NgSelectComponent;
  /** New-pricing dialog: land the cursor in Base price with its value selected. */
  @ViewChild('basePriceInput') set basePriceInputRef(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref) setTimeout(() => { ref.nativeElement.focus(); ref.nativeElement.select(); });
  }
  @ViewChild('vendorSelect') vendorSelect?: SearchSelectComponent;
  /** Holds a scanner's early Enter until its own search answers (see ScanTypeahead). */
  skuScan = new ScanTypeahead(() => this.skuScanSelect);
  eanScan = new ScanTypeahead(() => this.eanScanSelect);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private poService = inject(PoService);
  private vendorService = inject(VendorService);
  private productService = inject(ProductsService);
  private http = inject(HttpClient);
  // Preview State
  showPreview = false;
  previewUrl: SafeResourceUrl | null = null;
  private rawBlobUrl: string | null = null;
  isLoadingPreview = false;
  private confirmDialog = inject(ConfirmDialogService);
  private sanitizer = inject(DomSanitizer);
  private isSaved = false;
  private indentListService = inject(IndentListService);
  private authService = inject(AuthService);
  private zone = inject(NgZone);
  todayDate = new Date().toISOString().split('T')[0];
  defaultDeliveryDate = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  defaultExpiryDate = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  poForm!: FormGroup;
  poItems: any[] = [];
  manufacturers: any[] = [];
  vendors: any[] = [];
  brands: any[] = [];
  departments: any[] = [];
  categories: any[] = [];
  subCategories: any[] = [];
  products: any[] = [];
  productSearchTerm: string = '';
  selectedVendor: any;
  selectedManufacturers: string[] = [];
  selectedBrands: string[] = [];
  selectedDepartment: string = '';
  selectedCategory: string | null = null;
  selectedSubCategories: string[] = [];
  allCostPrices: number[] = [];
  allMRPs: number[] = [];
  deliveryDate: string = this.defaultDeliveryDate;
  expiryDate: string = this.defaultExpiryDate;
  freightCharges: number = 0;
  roundOffAmount: number = 0;
  schemameDiscountAmount: number = 0;
  fixedDiscountAmount: number = 0;
  // Upload
  isUploading = false;
  uploadErrors: Array<{ row: number; code: string; error: string }> = [];

  // Add to your existing properties
  productCodeSearch$ = new Subject<string>();
  searchResults: any[] = [];
  isCodeSearching = false;
  /** "Search by EAN" box — resolves identity barcodes and price-generation EANs. */
  productEanSearch$ = new Subject<string>();
  eanSearchResults: any[] = [];
  isEanSearching = false;
  selectedSearchType: 'sku' | 'plu' | 'ean' | 'query' = 'sku';
  searchTypes = [
    { value: 'sku', label: 'SKU' },
    { value: 'plu', label: 'PLU' },
    { value: 'ean', label: 'EAN' },
    { value: 'query', label: 'General' },
  ];
  mode = 'create';
  poId: string = '';
  today = new Date().toISOString().split('T')[0];
  summary = {
    totalItems: 0,
    totalQuantity: 0,
    totalBasicCost: 0,
    totalGstAmount: 0,
    totalPoValue: 0,
    schemeDiscountAmount: 0,
    fixedDiscountAmount: 0,
    totalCessAmount: 0,
    lineItemDiscountAmount: 0,
  };
  productSearch$ = new Subject<string>();
  selectedProduct: any = null;
  selectedVariant: any = null;
  selectedQuantity: number = 1;
  isSearching = false;
  enteredCostPrice: number = 0;
  MRP: number = 0;
  // The store/warehouse this PO is raised for. Display-only: it follows the
  // header switcher for a new PO and the saved PO's own location when
  // editing/viewing. locationId feeds product-search SOH (this used to hold a
  // STOREID, which matched no location and zeroed SOH); storeId goes on the
  // PO payload.
  selectedWarehouse: WarehouseOption | null = null;
  documentLocationId: string | null = null;
  sourceLocationId: string = '';
  baseUrl = environment.apiUrl;
  fromDate: string = '';
  selectedIndents: string[] = [];
  indentOptions: { id: string; label: string }[] = [];

  // ── Page UI state (design: collapsible header, line filters, bulk dialog) ──
  headerOpen = true;
  lineFilter: 'all' | 'indent' | 'manual' = 'all';
  lineQuery = '';
  bulkOpen = false;
  bulkFile: File | null = null;
  dragOver = false;

  // New Pricing Modal State
  isPricingFormVisible = false;
  editingItem: any = null; // ✅ Track which item is being edited
  newPricing = {
    basePrice: 0,
    costPrice: 0,
    sellingPrice: 0,
    mrp: 0,
  };
  newPricingValidationError: string | null = null;
  // CP-from-MRP is opt-in per popup: OFF by default so keying an MRP never
  // silently rewrites a manually entered cost price. When enabled, the
  // margin % (seeded from the product master) drives CP = MRP * (1 - m/100).
  useMarginForCp = false;
  pricingMarginPercent = 0;
  // "Selling price same as MRP?" is asked once per popup, the first time an
  // MRP is entered while the selling price is still empty. A yes keeps SP
  // tracking MRP until the user types an SP of their own.
  private sellingAtMrpAsked = false;
  sellingFollowsMrp = false;

  // ── Vendor ↔ SKU mapping (setting po.vendorSkuMappingMode) ──────────────
  // STRICT: the add-product searches are scoped to the vendor's SKUs (the
  // long-standing behaviour). PROMPT / REQUIRED: any SKU can be added; lines
  // not mapped to the vendor are badged, and on submit the buyer decides per
  // SKU whether to map it to the vendor, post it unmapped (PROMPT only) or
  // drop it. The server applies the same rule when the PO is posted.
  private configurationService = inject(ConfigurationService);
  vendorSkuMappingMode: VendorSkuMappingMode = 'STRICT';
  mappingDialog: VendorSkuMappingDialogState | null = null;
  mappingCheckPending = false;
  private vendorMappingRefresh$ = new Subject<void>();
  /** Vendor the mapping badges were checked against, and the variants already
   *  checked for it. Removing lines or editing quantities needs no new check
   *  (remaining lines keep their badges); only newly added variants are sent. */
  private mappingCheckedVendor: string | null = null;
  private mappingCheckedIds = new Set<string>();
  /** Decisions collected from a server PO_UNMAPPED_SKUS rejection, used on the resubmit. */
  private pendingMappingDecisions: { map: string[]; skip: string[] } | null = null;

  // ── Vendor first ─────────────────────────────────────────────────────────
  // A new PO opens with a vendor picker: lines are always raised against a
  // vendor (the mapping modes decide WHICH SKUs, never whether a vendor is
  // needed). Switching vendor once lines exist asks whether to keep them —
  // kept lines are re-checked against the new vendor for mapping.
  vendorPickerOpen = false;
  /** The picker is the page's entry step (Cancel leaves the page). */
  vendorPickerIsEntry = false;
  vendorPickerValue: string | null = null;
  vendorChange: { to: string | null; toName: string; fromName: string } | null = null;

  /** True when any active SKU may be put on the order, not just the vendor's. */
  get openVendorScope(): boolean {
    return this.vendorSkuMappingMode !== 'STRICT';
  }

  /** Vendor filter for the product searches: none in the open modes. */
  get vendorIdForSearch(): string | undefined {
    if (this.openVendorScope) return undefined;
    return this.selectedVendor || undefined;
  }

  /** Lines the last mapping check found NOT supplied by the selected vendor. */
  get unmappedLineCount(): number {
    return this.poItems.filter((i) => i?.vendorMapped === false).length;
  }

  get openScopeHint(): string {
    return this.vendorSkuMappingMode === 'REQUIRED'
      ? 'must be mapped to the vendor, or removed, before the PO is submitted'
      : 'will be offered for mapping to the vendor when the PO is submitted';
  }

  ngOnInit(): void {
    if (this.mode === 'view') {
      this.poForm.disable();
    }
    this.poForm = this.fb.group({
      poNumber: ['', Validators.required],
      deliveryDate: [this.deliveryDate],
      expiryDate: [this.expiryDate],
      packingCharges: [0],
      freightCharges: [0],
      roundOffAmount: [0],
      schemeDiscountAmount: [0],
      fixedDiscountAmount: [0],
      // false = basic + GST extra (B2B default). Auto-fills from vendor's
      // saved preference when a vendor is selected.
      costPriceIncludesGst: [false],
    });

    this.poForm
      .get('deliveryDate')
      ?.valueChanges.subscribe((selectedDate: string) => {
        if (!selectedDate) return;
        if (this.mode === 'view') return;

        const today = new Date();
        const chosenDate = new Date(selectedDate);

        console.log('Chosen Date:', chosenDate);

        if (chosenDate < today) {
          // ❌ Show error and reset deliveryDate to today
          this.toastr.error(
            'Delivery date cannot be earlier than today.',
            'Invalid Date',
          );
          const formattedToday = today.toISOString().split('T')[0];

          this.poForm.patchValue(
            { deliveryDate: formattedToday },
            { emitEvent: false },
          );
        }

        /** ✅ Step 4: Also validate expiryDate whenever deliveryDate changes */
        const expiryDateValue = this.poForm.get('expiryDate')?.value;
        if (expiryDateValue) {
          const expiryDate = new Date(expiryDateValue);
          if (expiryDate < chosenDate) {
            this.toastr.error(
              'Expiry date cannot be earlier than delivery date.',
              'Invalid Date',
            );
            this.poForm.patchValue(
              { expiryDate: chosenDate.toISOString().split('T')[0] },
              { emitEvent: false },
            );
          }
        }
      });

    /** ✅ Step 5: Watch for Expiry Date changes */
    this.poForm
      .get('expiryDate')
      ?.valueChanges.subscribe((selectedDate: string) => {
        if (!selectedDate) return;

        const deliveryDateValue = this.poForm.get('deliveryDate')?.value;
        if (!deliveryDateValue) return;

        const deliveryDate = new Date(deliveryDateValue);
        const expiryDate = new Date(selectedDate);

        if (expiryDate < deliveryDate) {
          // ❌ Show error and reset expiryDate to deliveryDate
          this.toastr.error(
            'Expiry date cannot be earlier than delivery date.',
            'Invalid Date',
          );
          const formattedDelivery = deliveryDate.toISOString().split('T')[0];
          this.poForm.patchValue(
            { expiryDate: formattedDelivery },
            { emitEvent: false },
          );
        }
      });

    const id = this.route.snapshot.paramMap.get('id');
    const view = this.route.snapshot.queryParamMap.get('view');
    if (id) {
      this.poId = id;
      this.mode = view === 'true' ? 'view' : 'edit';

      this.initDraft(id); // restore is offered once the PO has loaded
      this.loadPurchaseOrder();
    } else {
      this.mode = 'create';

      this.getNextPoNumber();
      this.initDraft(null);
      // After the first render: the restore question is a modal. The vendor
      // picker waits for it, and is not needed when the copy brought a vendor.
      setTimeout(async () => {
        await this.offerDraftRestore();
        if (!this.selectedVendor) this.openVendorPicker(true);
      });
    }

    this.productSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 3),
        tap(() => (this.isSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              query: term,
              context: 'PO',
              // Scope results to the selected vendor's products only (backend
              // filters Products.vendorsId for context 'PO'). Left undefined
              // until a vendor is chosen.
              vendorId: this.vendorIdForSearch,
            })
            .pipe(
              map((res: any) => res?.data || []),
              catchError(() => of([])),
              tap(() => (this.isSearching = false)),
            ),
        ),
      )
      .subscribe((data) => {
        this.products = data;
      });

    this.productCodeSearch$
      .pipe(
        // A new term makes the previous scan's rows stale: drop them so
        // neither Enter nor a click can pick the earlier product. No
        // distinctUntilChanged — scanning the same code again must search.
        tap((term) => {
          this.skuScan.termTyped(term);
          this.searchResults = [];
        }),
        debounceTime(300),
        filter((term) => !!term && term.length >= 2),
        tap(() => (this.isCodeSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              [this.selectedSearchType]: term,
              context: 'PO',
              locationId: this.sourceLocationId,
              // Scope SKU/PLU/EAN search to the selected vendor's products
              // only (backend filters Products.vendorsId for context 'PO').
              vendorId: this.vendorIdForSearch,
            })
            .pipe(
              map((res: any) => ({ term, data: res?.data || [] })),
              catchError(() => of({ term, data: [] })),
              tap(() => (this.isCodeSearching = false)),
            ),
        ),
      )
      .subscribe(({ term, data }) => {
        this.searchResults = data.map((item: any) => ({
          ...item,
          displayText: `${item.sku} - ${item.longDescription}`,
        }));
        this.skuScan.resultsArrived(term, this.searchResults);
      });

    this.productEanSearch$
      .pipe(
        // Same stale-row guard as the SKU box above.
        tap((term) => {
          this.eanScan.termTyped(term);
          this.eanSearchResults = [];
        }),
        debounceTime(300),
        filter((term) => !!term && term.trim().length >= 3),
        tap(() => (this.isEanSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              ean: term.trim(),
              context: 'PO',
              locationId: this.sourceLocationId,
              // Vendor-scoped like the SKU search above.
              vendorId: this.vendorIdForSearch,
            })
            .pipe(
              map((res: any) => ({ term, data: withMatchedEan(res?.data || [], term) })),
              catchError(() => of({ term, data: [] as any[] })),
              tap(() => (this.isEanSearching = false)),
            ),
        ),
      )
      .subscribe(({ term, data }) => {
        this.eanSearchResults = data;
        this.eanScan.resultsArrived(term, data);
      });

    this.configurationService
      .getValue(SETTING_KEYS.PO_VENDOR_SKU_MAPPING_MODE)
      .subscribe((value) => {
        this.vendorSkuMappingMode =
          value === 'PROMPT' || value === 'REQUIRED' ? value : 'STRICT';
        this.scheduleVendorMappingRefresh();
      });

    // Badge refresh is debounced and skipped when the set of lines has not
    // changed, so quantity edits (which also call updateSummary) cost nothing.
    this.vendorMappingRefresh$
      .pipe(debounceTime(400))
      .subscribe(() => this.refreshVendorMappingBadges());

    this.loadInitialData();
  }

  loadInitialData() {
    this.poService
      .getDepartments()
      .subscribe((res: any) => (this.departments = res?.data || []));
    this.vendorService
      .getAllVendorsList()
      .subscribe((res: any) => (this.vendors = res || []));
  }

  loadPurchaseOrder() {
    this.poService.getPoById(this.poId).subscribe((res: any) => {
      const poData = res || {};

      // A saved PO keeps ITS OWN store/warehouse, whatever the viewer is
      // currently working as.
      this.documentLocationId = poData?.store?.locationId ?? null;

      this.poForm.patchValue({
        poNumber: poData.poNumber,
        deliveryDate: poData.deliveryDate?.split('T')[0] || '',
        expiryDate: poData.expiryDate?.split('T')[0] || '',
        packingCharges: poData.packingCharges || 0,
        freightCharges: poData.freightCharges || 0,
        roundOffAmount: poData.roundOffAmount || 0,
        schemeDiscountAmount: poData.schemeDiscountAmount || 0,
        fixedDiscountAmount: poData.fixedDiscountAmount || 0,
      });

      this.fromDate = poData.indentDate || '';

      // 2. Load the selected indent IDs (used to set the multi-select's selected values)
      this.selectedIndents = poData.indentIds || [];

      // 3. Populate indentOptions (used to provide the display text for the selected IDs)
      if (poData.purchaseOrderIndents) {
        this.indentOptions = poData.purchaseOrderIndents.map((poi: any) => ({
          id: poi.indentId,
          label: `${poi.indent.indentNumber || '-'} | ${poi.indent.store?.name || '-'
            } | ${(poi.indent.createdAt || '').slice(0, 10)}`,
        }));

        // CRITICAL: If the indents are loaded, and the fromDate is set,
        // we should try to load all indents for that date in case the user wants to select a new one.
        if (this.fromDate) {
          this.loadAllIndentsForDate(this.fromDate);
        }
      }
      // Derive IDs from items if missing at root
      let manufacturerIds = poData.manufacturerIds || [];
      let brandIds = poData.brandIds || [];
      let departmentIds = poData.departmentIds || [];
      let categoryIds = poData.categoryIds || [];
      let subCategoryIds = poData.subCategoryIds || [];

      if (
        (!manufacturerIds || manufacturerIds.length === 0) &&
        poData.items?.length
      ) {
        manufacturerIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.manufacturerId)
              .filter((id: any) => !!id),
          ),
        ];
      }
      if ((!brandIds || brandIds.length === 0) && poData.items?.length) {
        brandIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.brandId)
              .filter((id: any) => !!id),
          ),
        ];
      }
      if (
        (!departmentIds || departmentIds.length === 0) &&
        poData.items?.length
      ) {
        departmentIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.departmentId) // Singular in product
              .filter((id: any) => !!id),
          ),
        ];
      }
      if ((!categoryIds || categoryIds.length === 0) && poData.items?.length) {
        categoryIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.categoryId) // Singular in product
              .filter((id: any) => !!id),
          ),
        ];
      }
      if (
        (!subCategoryIds || subCategoryIds.length === 0) &&
        poData.items?.length
      ) {
        subCategoryIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.subCategoryId) // Singular in product
              .filter((id: any) => !!id),
          ),
        ];
      }

      this.selectedVendor = poData.vendorId;

      // Restore the cost-price convention this PO was created with so
      // subsequent edits recompute under the same semantics.
      this.poForm.patchValue(
        { costPriceIncludesGst: !!poData.costPriceIncludesGst },
        { emitEvent: false },
      );

      // Assign derived or existing IDs
      this.selectedManufacturers = manufacturerIds;
      this.selectedBrands = brandIds;

      // Handle Department
      this.selectedDepartment = departmentIds;

      // Load Manufacturers and then populate Brands
      if (this.selectedVendor) {
        this.vendorService
          .getManufacturerDetails(this.selectedVendor)
          .subscribe((res: any) => {
            this.manufacturers = res || [];
            // The filter values are derived from the lines (a PO does not
            // store them). A line outside the vendor's manufacturers / brands
            // (open-scope modes allow that) has no option here, so its chip
            // would render blank — keep only values that have an option.
            this.selectedManufacturers = this.keepKnown(
              this.selectedManufacturers,
              this.manufacturers,
            );
            this.brands = this.manufacturers
              .filter((m) => this.selectedManufacturers.includes(m.id))
              .flatMap((m) => m.brands || []);
            this.selectedBrands = this.keepKnown(this.selectedBrands, this.brands);
          });
      }

      if (departmentIds.length > 0) {
        // Load categories for the first department found
        // Note: If multiple departments are selected, currently we only load for the first one due to API limitations or UI logic
        this.poService
          .getCategoriesByDepartment(departmentIds[0])
          .subscribe((res: any) => {
            this.categories = res?.data || [];
            if (
              this.selectedCategory &&
              !this.categories.some((c: any) => c.id === this.selectedCategory)
            ) {
              this.selectedCategory = null;
              this.selectedSubCategories = [];
              this.subCategories = [];
            }
          });
      }

      // Handle Category
      // UI uses single select for Category? Let's check.
      // HTML: [(ngModel)]="selectedCategory" ... [multiple] is NOT present.
      // So selectedCategory should be a single string (ID).
      // We pick the first one if multiple are found in items.
      this.selectedCategory = categoryIds.length > 0 ? categoryIds[0] : null;

      if (this.selectedCategory) {
        this.poService
          .getSubCategoriesByCategory(this.selectedCategory)
          .subscribe((res: any) => {
            this.subCategories = res?.data || [];
            // Lines from other categories bring sub categories this list
            // does not hold; drop those so no chip renders blank.
            this.selectedSubCategories = this.keepKnown(
              this.selectedSubCategories,
              this.subCategories,
            );
          });
      }

      this.selectedSubCategories = subCategoryIds;
      this.poItems = poData.items || [];
      this.poItems.forEach((item) => {
        if (item.id && !this.isValidUUID(item.id)) {
          console.warn(`Item id has invalid UUID:`, item.id);
        }
        item.sku =
          item?.productVariant?.sku ||
          item?.sku ||
          item?.productVariant?.product?.sku ||
          '';
        item.pricing = item?.productVariant?.pricing || item.pricing || [];
        item.variantName = item?.productVariant?.variantName || item.variantName || '';
        const gstPercent = item.gstPercent || 0;
        const cessRate = Number(item.cessRate || 0);
        const costPrice = item.costPrice || 0;
        const qty = Number(item.quantity || 0) + Number(item.indentQuantity || 0);
        const totalRate = gstPercent + cessRate;

        // actualCostPrice is the STORED basic cost price; only back-calc if
        // the field is missing on a legacy record.
        if (item.actualCostPrice == null || +item.actualCostPrice === 0) {
          item.actualCostPrice = +(costPrice / (1 + totalRate / 100)).toFixed(4);
        } else {
          item.actualCostPrice = Number(item.actualCostPrice);
        }

        item.gstAmount = Number(item.gstAmount || 0);
        item.cessAmount = Number(item.cessAmount || 0);

        // Back-fill basicCostTotal if missing on legacy records.
        if (item.basicCostTotal == null) {
          const lineGross = +(costPrice * qty).toFixed(3);
          item.basicCostTotal = +(lineGross - item.gstAmount - item.cessAmount).toFixed(3);
        }

        // PO lines store cost + MRP but no selling price: take SP (and a
        // missing MRP) from the price row this line was raised at, not the
        // variant's newest row, which may belong to another cost.
        const pricing = pricingForLine(item.pricing, costPrice, item.mrp) ?? item.pricing?.[0];
        item.mrp = Number(item.mrp || pricing?.mrp || 0);
        item.sellingPrice = Number(item.sellingPrice || pricing?.sellingPrice || 0);

        item.total = Number(item.totalPrice || item.total || 0);

        // ✅ Store metadata for New Pricing modal support from SKU clicks
        item.productData = item.productVariant?.product || item.productData || null;
        item.variantData = item.productVariant || item.variantData || null;
      });
      this.refreshDraftLinesFromMaster(poData);
      this.updateSummary();
      this.offerDraftRestore(poData?.updatedAt);
    });
  }

  ngOnDestroy(): void {
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  /**
   * DRAFT only: pull today's product-master values (GST %, cess, margin)
   * into every line and recompute its tax, so a slab corrected after the
   * draft was saved is reflected when the draft is reopened.
   *
   * The backend attaches the `current*` fields to DRAFT documents only, so a
   * POSTED PO keeps its snapshot untouched. Runs in view mode as well — a
   * draft opened read-only must still show today's rates, and nothing is
   * written until the user edits and saves. Prices the user typed (cost
   * price, MRP) are never overwritten.
   */
  private refreshDraftLinesFromMaster(poData: any): void {
    if (poData?.status !== 'DRAFT') return;

    const changed: string[] = [];
    for (const item of this.poItems) {
      const hasSnapshot =
        item.currentGstPercent != null || item.currentCessRate != null;
      if (!hasSnapshot) continue;

      const oldGst = +(item.gstPercent || 0);
      const oldCess = +(item.cessRate || 0);
      const newGst =
        item.currentGstPercent != null ? +item.currentGstPercent : oldGst;
      const newCess = +(item.currentCessRate ?? 0);
      const diffs: string[] = [];

      if (newGst !== oldGst) {
        diffs.push(`GST ${oldGst}% → ${newGst}%`);
        item.gstPercent = newGst;
      }
      if (newCess !== oldCess) {
        diffs.push(`Cess ${oldCess}% → ${newCess}%`);
        item.cessRate = newCess;
      }
      if (
        item.currentMarginPercent != null &&
        item.marginPercent != null &&
        +item.currentMarginPercent !== +item.marginPercent
      ) {
        diffs.push(`Margin ${+item.marginPercent}% → ${+item.currentMarginPercent}%`);
        item.marginPercent = +item.currentMarginPercent;
      }

      // Strip the snapshot so it never rides along into the save payload.
      for (const k of Object.keys(item)) {
        if (k.startsWith('current')) delete item[k];
      }

      if (diffs.length) {
        this.onQuantityChange(item); // recompute line tax + totals
        changed.push(`${item.sku || item.skuCode || ''} (${diffs.join(', ')})`);
      }
    }

    if (changed.length) {
      const shown = changed.slice(0, 5).join('; ');
      const more = changed.length > 5 ? ` and ${changed.length - 5} more` : '';
      this.toastr.info(
        `${changed.length} line(s) updated from the SKU master: ${shown}${more}. Save the draft to keep these values.`,
        'Draft refreshed',
        { timeOut: 10000 },
      );
    }
  }

  loadAllIndentsForDate(date: string): void {
    this.indentListService.getIndentByStoreIds([], date, 'POSTED').subscribe({
      next: (res: any[]) => {
        const list = Array.isArray(res) ? res : [];
        const newOptions = list.map((item: any) => ({
          id: item.id,
          label: `${item.indentNumber || '-'} | ${item.store?.name || '-'} | ${(
            item.createdAt || ''
          ).slice(0, 10)}`,
        }));

        // Merge existing selected indents (from PO data or previous selections) with all indents from the API
        const uniqueOptions = [...this.indentOptions];
        const existingIds = new Set(uniqueOptions.map((o) => o.id));

        newOptions.forEach((option) => {
          if (!existingIds.has(option.id)) {
            uniqueOptions.push(option);
          }
        });
        this.indentOptions = uniqueOptions;

        // Only show toast if the list was actually updated from the API
        if (newOptions.length > 0) {
          this.toastr.success('Available Indents updated for selected date');
        }
      },
      error: () => this.toastr.error('Failed to load indents from API'),
    });
  }

  /** Selected ids that have an option in `options` (by `id`). */
  private keepKnown(ids: string[] | null | undefined, options: any[]): string[] {
    const known = new Set((options || []).map((o) => o?.id));
    return (ids || []).filter((id) => known.has(id));
  }

  onManufacturersChange() {
    // Update brands list based on selected manufacturers
    this.brands = this.manufacturers
      .filter((m) => this.selectedManufacturers.includes(m.id))
      .flatMap((m) => m.brands || []);

    // Reset brand selection if current selection is not valid for new manufacturers
    if (this.selectedBrands && this.selectedBrands.length > 0) {
      const validBrandIds = this.brands.map((b) => b.id);
      this.selectedBrands = this.selectedBrands.filter((brandId) =>
        validBrandIds.includes(brandId),
      );
    }
  }

  onDepartmentChange() {
    this.categories = [];
    this.subCategories = [];
    this.selectedCategory = null;
    this.selectedSubCategories = [];
    if (this.poForm.get('category')) {
      this.poForm.get('category')?.reset(null);
    }
    if (this.poForm.get('subCategory')) {
      this.poForm.get('subCategory')?.reset([]);
    }

    if (this.selectedDepartment && this.selectedDepartment.length > 0) {
      this.poService
        .getCategoriesByDepartment(this.selectedDepartment)
        .subscribe((res: any) => {
          this.categories = res?.data || [];
          console.log('[DepartmentChange] Loaded Categories:', this.categories);
        });
    }
  }

  onCategoryChange() {
    // Reset dependent selections
    this.subCategories = [];
    this.selectedSubCategories = [];
    if (this.selectedCategory) {
      // Load subcategories for selected category
      this.poService
        .getSubCategoriesByCategory(this.selectedCategory)
        .subscribe((res: any) => {
          this.subCategories = res?.data || [];
        });
    }
  }

  onFromDateChange(): void {
    this.selectedIndents = [];
    this.indentOptions = [];
    // Load all available indents for the newly selected date
    this.loadAllIndentsForDate(this.fromDate);
  }
  loadFilteredProducts() {
    const filters: any = {};

    // Build filter object with current selections
    if (this.selectedVendor) filters.vendorId = this.selectedVendor;
    if (this.selectedManufacturers?.length)
      filters.manufacturerIds = this.selectedManufacturers;
    if (this.selectedBrands?.length) filters.brandIds = this.selectedBrands;
    if (this.selectedDepartment && this.selectedDepartment.length > 0)
      filters.departmentIds = [this.selectedDepartment];
    if (this.selectedCategory?.length)
      filters.categoryIds = [this.selectedCategory];
    if (this.selectedSubCategories?.length)
      filters.subCategoryIds = this.selectedSubCategories;
    if (this.selectedIndents?.length) filters.indentIds = this.selectedIndents;

    // Clean up empty filters
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete filters[key];
      }
    });

    // IMPORTANT: If no vendor selected, clear table and return
    if (!this.selectedVendor) {
      this.poItems = [];
      this.updateSummary();
      return;
    }

    // If only vendor is selected but no other filters, we might want to show all vendor products
    // or clear the table - depends on your business logic
    if (Object.keys(filters).length === 1 && filters.vendorId) {
      // Uncomment the line below if you want to clear table when only vendor is selected
      // this.poItems = [];
      // this.updateSummary();
      // return;
    }

    // Get filtered products from API
    this.poService.getFilteredProducts(filters).subscribe({
      next: (res: any) => {
        const data = res || [];

        // CRITICAL: Clear the existing table first - this ensures proper filtering
        this.poItems = [];

        // Add all filtered products to table
        let addedCount = 0;
        let mergedCount = 0;
        const unpriced: string[] = [];
        for (const product of data) {
          const variants = product.variants || [];
          for (const variant of variants) {
            const merged = this.addItemToTable(product, variant);
            if (merged === null) unpriced.push(product.sku || product.longDescription);
            else if (merged) mergedCount++;
            else addedCount++;
          }
        }

        // Update summary after adding all items
        this.updateSummary();

        if (unpriced.length) {
          this.toastr.warning(
            `${unpriced.length} SKU(s) skipped — no cost price / MRP set yet: ${unpriced.slice(0, 5).join(', ')}${unpriced.length > 5 ? ', …' : ''}`,
            'Price required',
          );
        }

        // Show user feedback
        if (addedCount === 0 && mergedCount === 0) {
          this.toastr.info(
            'No items found matching the selected filters',
            'No Results',
          );
        } else if (mergedCount > 0) {
          this.toastr.success(
            `${addedCount} item(s) loaded; ${mergedCount} duplicate SKU(s) already in the list were merged (quantities summed).`,
            'Items Loaded',
          );
        } else {
          this.toastr.success(
            `${addedCount} items loaded based on current filters`,
            'Items Loaded',
          );
        }
      },
      error: (error) => {
        console.error('Error loading filtered products:', error);
        this.toastr.error('Failed to load filtered products', 'Error');
      },
    });
  }

  vendorNameById(id: string | null | undefined): string {
    if (!id) return '';
    return this.vendors.find((v: any) => v.id === id)?.name || '';
  }

  openVendorPicker(asEntry = false): void {
    if (this.isViewMode) return;
    this.vendorPickerIsEntry = asEntry;
    this.vendorPickerValue = this.selectedVendor || null;
    this.vendorPickerOpen = true;
  }

  confirmVendorPicker(): void {
    if (!this.vendorPickerValue) return;
    this.vendorPickerOpen = false;
    this.vendorPickerIsEntry = false;
    this.onVendorSelected(this.vendorPickerValue);
  }

  /** Focus after the current keystroke and change detection settle (the
   *  button is still [disabled] until vendorPickerValue renders). */
  focusLater(el: HTMLElement): void {
    setTimeout(() => el.focus());
  }

  cancelVendorPicker(): void {
    this.vendorPickerOpen = false;
    if (this.vendorPickerIsEntry && !this.selectedVendor && !this.poItems.length) {
      this.router.navigate(['/po']);
    }
    this.vendorPickerIsEntry = false;
  }

  /** Header vendor select: switching away from a vendor with lines asks first. */
  onVendorSelected(next: string | null): void {
    next = next || null;
    if (next === (this.selectedVendor || null)) return;
    if (!this.poItems.length) {
      this.applyVendor(next, false);
      return;
    }
    this.vendorChange = {
      to: next,
      toName: this.vendorNameById(next),
      fromName: this.selectedVendorName,
    };
  }

  resolveVendorChange(choice: 'keep' | 'clear' | 'cancel'): void {
    const change = this.vendorChange;
    this.vendorChange = null;
    if (!change) return;
    if (choice === 'cancel') {
      // The select already shows the new pick; put the current vendor back.
      this.vendorSelect?.writeValue(this.selectedVendor || null);
      return;
    }
    this.applyVendor(change.to, choice === 'keep');
  }

  private applyVendor(next: string | null, keepLines: boolean): void {
    this.selectedVendor = next;
    this.vendorSelect?.writeValue(next);
    this.onVendorChange(keepLines);
  }

  onVendorChange(keepLines = false) {
    this.uploadErrors = [];
    if (this.selectedVendor) {
      if (keepLines) {
        // Mapping badges belonged to the previous vendor — re-check them.
        for (const item of this.poItems) delete item.vendorMapped;
        this.resetVendorMappingCache();
        this.scheduleVendorMappingRefresh();
        // The vendor's filters no longer apply.
        this.selectedManufacturers = [];
        this.selectedBrands = [];
        this.brands = [];
        this.loadVendorManufacturerDetails(this.selectedVendor);
        // Kept lines keep the cost-price basis they were keyed in.
        return;
      }
      this.poItems = [];
      this.updateSummary();
      this.selectedManufacturers = [];
      this.selectedBrands = [];
      this.brands = [];
      this.loadVendorManufacturerDetails(this.selectedVendor);
      // Pull the vendor's cost-price convention as the default for this PO.
      const vendor = this.vendors.find((v: any) => v.id === this.selectedVendor);
      if (vendor) {
        this.poForm.patchValue(
          { costPriceIncludesGst: !!vendor.costPriceIncludesGst },
          { emitEvent: false },
        );
      }
    } else {
      this.manufacturers = [];
      this.brands = [];
      this.categories = [];
      this.subCategories = [];
      this.products = [];
      this.selectedManufacturers = [];
      this.selectedBrands = [];
      this.selectedCategory = '';
      this.selectedSubCategories = [];
      if (keepLines) {
        for (const item of this.poItems) delete item.vendorMapped;
        this.resetVendorMappingCache();
        return;
      }
      this.poItems = []; // ✅ Clear the table
      this.updateSummary(); // ✅ Reset summary
    }
  }

  downloadTemplate() {
    if (!this.selectedVendor) return;
    this.poService.downloadTemplate(this.selectedVendor).subscribe({
      next: (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'po_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: () => this.toastr.error('Failed to download template.'),
    });
  }

  onFileSelected(event: any) {
    const file: File = event.target.files?.[0];
    this.uploadLineItems(file, () => (event.target.value = ''));
  }

  /** Parse an XLSX of line items against the selected vendor and append rows. */
  uploadLineItems(file: File | null | undefined, done?: (ok: boolean) => void) {
    if (!file || !this.selectedVendor) return;
    this.isUploading = true;
    this.uploadErrors = [];
    this.poService.parseUpload(file, this.selectedVendor).subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.uploadErrors = res.errors || [];
        // Apply vendor's cost-price convention so onQuantityChange below
        // recomputes each row under the right semantics.
        if (typeof res.costPriceIncludesGst === 'boolean') {
          this.poForm.patchValue(
            { costPriceIncludesGst: res.costPriceIncludesGst },
            { emitEvent: false },
          );
        }
        const validRows: any[] = res.rows || [];
        for (const row of validRows) {
          const existingIndex = this.poItems.findIndex(
            (i) => i.variantId === row.variantId,
          );
          if (existingIndex >= 0) {
            this.poItems[existingIndex].quantity += row.quantity;
            if (typeof row.vendorMapped === 'boolean')
              this.poItems[existingIndex].vendorMapped = row.vendorMapped;
            this.onQuantityChange(this.poItems[existingIndex]);
          } else {
            const newItem = {
              variantId: row.variantId,
              productVariantId: row.variantId,
              productId: row.productId,
              // Outside STRICT mode the sheet may carry SKUs the vendor does
              // not supply yet; they are badged and offered for mapping on submit.
              vendorMapped:
                typeof row.vendorMapped === 'boolean' ? row.vendorMapped : undefined,
              sku: row.code,
              skuCode: row.code,
              variantName: row.name,
              quantity: row.quantity,
              freeQuantity: row.freeQty,
              indentQuantity: 0,
              discountPercent: row.discountPercent,
              discountAmount: +(
                (Number(row.costPrice) * Number(row.discountPercent)) /
                100
              ).toFixed(2),
              costPrice: row.costPrice,
              actualCostPrice: row.actualCostPrice,
              mrp: row.mrp,
              sellingPrice: row.sellingPrice || 0,
              gstPercent: row.gstPercent,
              gstAmount: row.gstAmount,
              cessRate: row.cessRate,
              cessAmount: row.cessAmount,
              total: row.total,
              pricing: row.pricing || [
                {
                  costPrice: row.costPrice,
                  mrp: row.mrp,
                  sellingPrice: row.sellingPrice || 0,
                },
              ],
              productData: {
                id: row.productId,
                marginPercentage: row.marginPercentage,
                purchaseGstRate: { value: String(row.gstPercent) },
                cessRate: String(row.cessRate ?? 0),
              },
              variantData: {
                id: row.variantId,
                pricing: row.pricing || [
                  {
                    costPrice: row.costPrice,
                    mrp: row.mrp,
                    sellingPrice: row.sellingPrice || 0,
                  },
                ],
              },
            };
            this.poItems.push(newItem);
            this.onQuantityChange(newItem);
          }
        }
        this.updateSummary();
        if (validRows.length > 0)
          this.toastr.success(`${validRows.length} item(s) loaded from file.`);
        const unmappedRows = validRows.filter((r) => r.vendorMapped === false).length;
        if (unmappedRows > 0)
          this.toastr.info(
            `${unmappedRows} uploaded SKU(s) are not mapped to ${this.selectedVendorName || 'this vendor'} — they ${this.openScopeHint}.`,
            'Unmapped SKUs',
            { timeOut: 8000 },
          );
        if (res.errors?.length > 0)
          this.toastr.warning(
            `${res.errors.length} row(s) had errors — see details below.`,
          );
        done?.(true);
      },
      error: (err: any) => {
        this.isUploading = false;
        this.toastr.error(err?.error?.message || 'Upload failed.');
        done?.(false);
      },
    });
  }

  // ── Bulk line items dialog ─────────────────────────────────────────────
  openBulk(): void {
    if (!this.selectedVendor) {
      this.toastr.info('Select a vendor first — the template is scoped to its SKUs.');
      return;
    }
    this.bulkFile = null;
    this.dragOver = false;
    this.bulkOpen = true;
  }

  closeBulk(): void {
    if (this.isUploading) return;
    this.bulkOpen = false;
    this.bulkFile = null;
  }

  onBulkFilePicked(event: any): void {
    const file: File | undefined = event.target.files?.[0];
    if (file) this.bulkFile = file;
    event.target.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onBulkDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      this.toastr.warning('Please drop an .xlsx or .xls file.');
      return;
    }
    this.bulkFile = file;
  }

  importBulk(): void {
    if (!this.bulkFile) return;
    this.uploadLineItems(this.bulkFile, (ok) => {
      if (ok) {
        this.bulkOpen = false;
        this.bulkFile = null;
      }
    });
  }

  onDiscountChange(item: any) {
    const discPct = Number(item.discountPercent || 0);
    item.discountAmount = +(
      (Number(item.costPrice || 0) * discPct) /
      100
    ).toFixed(2);
    this.onQuantityChange(item);
  }

  loadVendorManufacturerDetails(vendorId: any) {
    this.vendorService
      .getManufacturerDetails(vendorId)
      .subscribe((res: any) => {
        this.manufacturers = res || [];
      });
  }

  searchProducts(type: string, term: string): Observable<any[]> {
    if (!term || term.length < 2) {
      return of([]); // Return an empty observable if term is too short
    }

    const params = new HttpParams().set('type', type).set('query', term);

    return this.http.get<any[]>('http://your-api/search', { params });
  }

  /**
   * Returns true when merged into an existing line, false when a new line was
   * added, and null when the variant has no usable price yet (barcode tagged,
   * pricing deferred) — a PO line at ₹0 must never be created silently.
   */
  addItemToTable(product: any, variant: any): boolean | null {
    if (!hasPurchasePrice(variant)) return null;

    const gstRate = parseFloat(product?.purchaseGstRate?.value || '0');
    const pricing0 = variant?.pricing?.[0] || {};
    const costPrice = parseFloat(pricing0.costPrice || '0');
    // Stored basic when it agrees with this cost price, else derived.
    const basicCostPrice = consistentBasicCost(
      costPrice,
      gstRate + (parseFloat(product?.cessRate || '0') || 0),
      pricing0.basePrice,
    );
    const cessRate = parseFloat(product?.cessRate || '0');
    let quantity = product?.innerPackaging || 1;
    const indentQty = product.indentTotalQty || 0;
    if (indentQty > 0) {
      quantity = 0;
    }
    const totalQty = quantity + indentQty;
    const breakup = this.computeLineTaxBreakup(
      basicCostPrice,
      costPrice,
      totalQty,
      gstRate,
      cessRate || 0,
    );
    const actualCost = breakup.actualCostPrice;
    const basicCostTotal = breakup.basicCostTotal;
    const gstAmount = breakup.gstAmount;
    const cessAmount = breakup.cessAmount;
    const total = breakup.lineGross;

    // Create item with basic properties - API filtering means we don't need complex metadata
    const newItem = {
      // Core properties for PO functionality
      pricing: variant.pricing || [],
      productVariantId: variant.id,
      variantName: variant.variantName,
      quantity,
      costPrice,
      actualCostPrice: actualCost,
      mrp: Number(variant.pricing?.[0]?.mrp || 0),
      sellingPrice: Number(variant.pricing?.[0]?.sellingPrice || 0),
      gstPercent: gstRate,
      gstAmount,
      total,
      basicCostTotal,
      innerPackaging: product?.innerPackaging || 1,
      cessRate,
      cessAmount,
      freeQuantity: variant.i || 0,
      indentQuantity: product.indentTotalQty || 0,

      // ✅ Add SKU codes for table display
      sku: variant.sku || product.sku || '',

      // Optional: Store minimal metadata for display purposes only
      productId: product.id,
      manufacturerName: product.manufacturer?.name || '',
      brandName: product.brand?.name || '',
      departmentName: product.department?.name || '',
      categoryName: product.category?.name || '',
      subCategoryName: product.subCategory?.name || '',

      // Store full product data for reference if needed
      productData: product,
      variantData: variant,
      marginPercentage: product.marginPercentage || 0,
    };

    // A PO/GRN should carry ONE line per SKU. When the same variant arrives
    // again (e.g. it is on more than one selected indent), merge into the
    // existing row and sum the quantities instead of adding a duplicate line.
    const existingIndex = this.poItems.findIndex(
      (i) => i.productVariantId === newItem.productVariantId,
    );
    if (existingIndex !== -1) {
      const existing = this.poItems[existingIndex];
      existing.quantity = Number(existing.quantity || 0) + Number(quantity || 0);
      existing.indentQuantity =
        Number(existing.indentQuantity || 0) + Number(newItem.indentQuantity || 0);
      existing.freeQuantity =
        Number(existing.freeQuantity || 0) + Number(newItem.freeQuantity || 0);
      this.onQuantityChange(existing);
      this.updateSummary();
      return true; // merged a duplicate
    }

    this.poItems.push(newItem);
    this.updateSummary();
    return false; // new row
  }

  removeItem(index: number) {
    this.selectedItems.delete(this.poItems[index]);
    this.poItems.splice(index, 1);
    this.updateSummary();
  }

  // Multi-select line items for bulk delete
  selectedItems = new Set<any>();

  isItemSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  toggleItemSelection(item: any): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  get selectedCount(): number {
    return this.poItems.reduce(
      (n: number, it: any) => n + (this.selectedItems.has(it) ? 1 : 0),
      0
    );
  }

  get allItemsSelected(): boolean {
    return this.poItems.length > 0 && this.selectedCount === this.poItems.length;
  }

  toggleSelectAll(checked: boolean): void {
    this.selectedItems.clear();
    if (checked) {
      this.poItems.forEach((it: any) => this.selectedItems.add(it));
    }
  }

  // ── Line grid helpers (filters, counts, per-row delete) ────────────────
  /** Rows shown in the grid after the All / From indents / Manual pills and the text filter. */
  get visibleItems(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    this.poItems.forEach((item, index) => {
      const fromIndent = Number(item?.indentQuantity || 0) > 0;
      if (this.lineFilter === 'indent' && !fromIndent) return;
      if (this.lineFilter === 'manual' && fromIndent) return;
      if (q) {
        const hay = `${item?.sku || ''} ${item?.skuCode || ''} ${item?.variantName || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get indentLineCount(): number {
    return this.poItems.filter((i) => Number(i?.indentQuantity || 0) > 0).length;
  }

  get manualLineCount(): number {
    return this.poItems.length - this.indentLineCount;
  }

  get lineCountLabel(): string {
    const shown = this.visibleItems.length;
    const all = this.poItems.length;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  get totalOrderedQty(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.quantity || 0), 0);
  }

  get totalIndentQty(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.indentQuantity || 0), 0);
  }

  get totalFreeQty(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.freeQuantity || 0), 0);
  }

  /** Sum of line gross values before the order-level scheme / fixed discounts. */
  get grossLineTotal(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.total || 0), 0);
  }

  /** Line discounts + scheme + fixed — what the summary strip shows as DISCOUNT. */
  get totalDiscount(): number {
    return (
      Number(this.summary.lineItemDiscountAmount || 0) +
      Number(this.poForm?.value?.schemeDiscountAmount || 0) +
      Number(this.poForm?.value?.fixedDiscountAmount || 0)
    );
  }

  get selectedVendorName(): string {
    if (!this.selectedVendor) return '';
    const id = typeof this.selectedVendor === 'object' ? this.selectedVendor?.id : this.selectedVendor;
    return this.vendors.find((v) => v.id === id)?.name || '';
  }

  get selectedManufacturerLabel(): string {
    const ids = this.selectedManufacturers || [];
    if (!ids.length) return '';
    const names = ids
      .map((id) => this.manufacturers.find((m) => m.id === id)?.name)
      .filter(Boolean) as string[];
    if (!names.length) return '';
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  }

  get categoryLabel(): string {
    const dep = (this.selectedDepartment as any) || [];
    const depIds: string[] = Array.isArray(dep) ? dep : dep ? [dep] : [];
    const depName = depIds.length
      ? this.departments.find((d) => d.id === depIds[0])?.name
      : '';
    const catName = this.selectedCategory
      ? this.categories.find((c) => c.id === this.selectedCategory)?.name
      : '';
    return [depName, catName].filter(Boolean).join(' › ');
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
    this.lineQuery = '';
  }

  clearSelectedProduct(): void {
    this.selectedProduct = null;
    this.selectedVariant = null;
    this.selectedQuantity = 1;
    this.enteredCostPrice = 0;
    this.MRP = 0;
    this.allCostPrices = [];
    this.allMRPs = [];
  }

  removeLine(item: any): void {
    if (this.isViewMode) return;
    const idx = this.poItems.indexOf(item);
    if (idx < 0) return;
    this.poItems.splice(idx, 1);
    this.selectedItems.delete(item);
    this.updateSummary();
  }

  async clearLines(): Promise<void> {
    if (this.isViewMode || !this.poItems.length) return;
    const n = this.poItems.length;
    const proceed = await this.confirmDialog.confirm({
      title: `Clear all ${n} line item${n === 1 ? '' : 's'}?`,
      message:
        'Quantities, free quantities, discounts and pricing entered on these lines will be removed. Load products from the indents again to start over.',
      confirmText: 'Clear lines',
      cancelText: 'Cancel',
      tone: 'neutral',
      icon: 'bi-eraser',
    });
    if (!proceed) return;
    this.poItems = [];
    this.selectedItems.clear();
    this.clearLineFilters();
    this.updateSummary();
  }

  /** Close the pricing dialog; when it was opened from a grid row, drop the
   *  row's product from the add-line strip so the strip does not linger. */
  closePricingForm(): void {
    this.isPricingFormVisible = false;
    if (this.editingItem) {
      this.editingItem = null;
      this.clearSelectedProduct();
    }
  }

  /** Pricing dialog: apply one of the SKU's known prices to the line being edited. */
  applyKnownPrice(p: any): void {
    if (!this.editingItem || !p) return;
    this.editingItem.costPrice = p.costPrice;
    if (p.mrp != null) this.editingItem.mrp = Number(p.mrp);
    if (p.sellingPrice != null) this.editingItem.sellingPrice = Number(p.sellingPrice);
    this.onQuantityChange(this.editingItem);
    this.updateSummary();
    this.closePricingForm();
  }

  async removeSelectedItems(): Promise<void> {
    const count = this.selectedCount;
    if (!count) return;
    const proceed = await this.confirmDialog.confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${count} selected item${count > 1 ? 's' : ''}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!proceed) return;
    for (let i = this.poItems.length - 1; i >= 0; i--) {
      if (this.selectedItems.has(this.poItems[i])) {
        this.poItems.splice(i, 1);
      }
    }
    this.selectedItems.clear();
    this.updateSummary();
  }

  onQuantityChange(item: any): void {
    const qty = Number(item.quantity || 0);
    const indentQty = Number(item.indentQuantity || 0);
    // freeQuantity does NOT contribute to the cost, so we don't add it to totalChargedQty
    const freeQty = Number(item.freeQuantity || 0);

    const costPrice = Number(item.costPrice || 0);
    const gstPercent = Number(item.gstPercent || 0);
    const cessRate = Number(item.cessRate || 0);

    // Total quantity that we are paying for
    const totalChargedQty = qty + indentQty;

    // ✅ Robust Sync: Match MRP, Selling Price AND basicCostPrice from item.pricing
    // when the user changes the selected costPrice (dropdown). Each pricing row
    // has its own paired basePrice — we must follow it, not derive.
    if (item.pricing && item.pricing.length > 0) {
      const match = item.pricing.find((p: any) => +p.costPrice === +costPrice);
      if (match) {
        item.mrp = Number(match.mrp || 0);
        item.sellingPrice = Number(match.sellingPrice || 0);
      }
    }

    // Stored basic cost price — never derived from costPrice at runtime.
    const basicCostPrice = this.resolveBasicCostPrice(item);
    item.actualCostPrice = basicCostPrice;

    const breakup = this.computeLineTaxBreakup(
      basicCostPrice,
      costPrice,
      totalChargedQty,
      gstPercent,
      cessRate,
    );
    item.gstAmount = breakup.gstAmount;
    item.cessAmount = breakup.cessAmount;
    item.basicCostTotal = breakup.basicCostTotal;

    // Recompute discount amount from current cost price and discount %
    const discPct = Number(item.discountPercent || 0);
    item.discountAmount = +((costPrice * discPct) / 100).toFixed(2);

    // Total = line gross − line-level discount
    item.total = +(breakup.lineGross - item.discountAmount * totalChargedQty).toFixed(3);

    this.updateSummary();
  }

  /**
   * Single source of truth for PO line tax math (mirrors backend po-tax.util.ts).
   * Both basicCostPrice and costPrice are STORED inputs — neither is derived
   * from the other. The toggle only chooses which one drives the line total
   * to match the vendor's invoice. The per-unit actualCostPrice returned
   * equals the stored basicCostPrice in both modes.
   */
  computeLineTaxBreakup(
    basicCostPrice: number,
    costPrice: number,
    qty: number,
    gstRate: number,
    cessRate: number = 0,
  ): {
    lineGross: number;
    gstAmount: number;
    cessAmount: number;
    basicCostTotal: number;
    actualCostPrice: number;
  } {
    const r2 = (n: number) => +(+n).toFixed(2);
    // r3: price-derived line totals keep 3 decimals so a CP like 85.501
    // survives into the line totals instead of displaying 85.50 while
    // validation sees 85.501. Tax amounts stay at 2 decimals.
    const r3 = (n: number) => +(+n).toFixed(3);
    // r4: per-unit rate retains 4 decimals so derived per-base-unit prices
    // (e.g., per-kg from per-bag) don't lose precision once multiplied by qty.
    const r4 = (n: number) => +(+n).toFixed(4);
    const bp = Number(basicCostPrice) || 0;
    const cp = Number(costPrice) || 0;
    const q = Number(qty) || 0;
    const g = Number(gstRate) || 0;
    const c = Number(cessRate) || 0;
    const totalRate = g + c;
    const inclusive = !!this.poForm?.value?.costPriceIncludesGst;

    if (inclusive) {
      const lineGross = r3(cp * q);
      const gstAmount = r2((lineGross * g) / (100 + totalRate));
      const cessAmount = r2((lineGross * c) / (100 + totalRate));
      const basicCostTotal = r3(lineGross - gstAmount - cessAmount);
      return { lineGross, gstAmount, cessAmount, basicCostTotal, actualCostPrice: r4(bp) };
    }

    const basicCostTotal = r3(bp * q);
    const gstAmount = r2((basicCostTotal * g) / 100);
    const cessAmount = r2((basicCostTotal * c) / 100);
    const lineGross = r3(basicCostTotal + gstAmount + cessAmount);
    return { lineGross, gstAmount, cessAmount, basicCostTotal, actualCostPrice: r4(bp) };
  }

  /**
   * Resolves the per-unit basic cost price (excl GST) for an item.
   * Prefers the stored item.actualCostPrice → variant.pricing[0].basePrice →
   * back-calc from costPrice as last resort (for very old records that
   * predate basePrice being stored).
   */
  private resolveBasicCostPrice(item: any): number {
    const cost = Number(item?.costPrice ?? 0);
    const gst = Number(item?.gstPercent ?? 0);
    const cess = Number(item?.cessRate ?? 0);
    // The price row matching the selected cost, then the stored line basic —
    // each only if it agrees with the cost — else derived. The stored basic
    // can belong to another row (cost picked from the dropdown afterwards).
    const rows = item?.pricing?.length ? item.pricing : item?.variantData?.pricing;
    return consistentBasicCost(
      cost,
      gst + cess,
      pricingForCost(rows, cost)?.basePrice,
      item?.actualCostPrice,
    );
  }

  get costPriceIncludesGst(): boolean {
    return !!this.poForm?.value?.costPriceIncludesGst;
  }

  setCostBasis(includesGst: boolean): void {
    if (this.isViewMode || this.costPriceIncludesGst === includesGst) return;
    this.poForm.patchValue({ costPriceIncludesGst: includesGst });
    this.poForm.markAsDirty();
    this.onCostPriceModeChange();
  }

  /** Recompute all line items when the toggle flips. */
  onCostPriceModeChange(): void {
    for (const item of this.poItems) {
      this.onQuantityChange(item);
    }
    this.updateSummary();
  }

  updateSummary(): void {
    let totalItems = this.poItems.length;
    let totalQuantity = 0;
    let totalBasicCost = 0;
    let totalGstAmount = 0;
    let totalPoValue = 0;
    let totalCessAmount = 0;
    let lineItemDiscountAmount = 0;

    this.poItems.forEach((item) => {
      const qty = Number(item.quantity || 0);
      const indentQty = Number(item.indentQuantity || 0);
      const totalChargedQty = qty + indentQty;

      totalQuantity += qty + Number(item.freeQuantity || 0);
      // Use the back-calculated line taxable (CGST Rule 35) — never re-multiply
      // a rounded per-unit base, which would introduce drift.
      totalBasicCost += Number(item.basicCostTotal || 0);
      totalGstAmount += Number(item.gstAmount || 0);
      totalCessAmount += Number(item.cessAmount || 0);
      totalPoValue += Number(item.total || 0);
      lineItemDiscountAmount += Number(item.discountAmount || 0) * totalChargedQty;
    });

    const schemeDiscount = Number(this.poForm.value.schemeDiscountAmount) || 0;
    const fixedDiscount = Number(this.poForm.value.fixedDiscountAmount) || 0;
    const grandTotal = +(totalPoValue - schemeDiscount - fixedDiscount).toFixed(2);

    this.summary = {
      totalItems,
      totalQuantity,
      totalBasicCost: +totalBasicCost.toFixed(2),
      totalGstAmount: +totalGstAmount.toFixed(2),
      totalCessAmount: +totalCessAmount.toFixed(2),
      totalPoValue: +grandTotal.toFixed(2),
      schemeDiscountAmount: schemeDiscount,
      fixedDiscountAmount: fixedDiscount,
      lineItemDiscountAmount: +lineItemDiscountAmount.toFixed(2),
    };
    this.scheduleVendorMappingRefresh();
  }

  getNextPoNumber() {
    this.poService
      .getNextPoNumber(this.selectedWarehouse?.storeId ?? undefined)
      .subscribe((res: any) => {
        this.poForm.patchValue({ poNumber: res?.poNumber });
      });
  }

  onWarehouseChange(warehouse: WarehouseOption | null) {
    this.selectedWarehouse = warehouse;
    this.sourceLocationId = warehouse?.locationId ?? '';
    // The number prefix follows the chosen warehouse's store code.
    if (this.mode === 'create') {
      this.getNextPoNumber();
    }
  }

  loadPreview(poId: string) {
    console.log('loadPreview called for ID:', poId);
    this.showPreview = true;
    this.isLoadingPreview = true;

    this.poService.downloadPurchaseOrder(poId).subscribe({
      next: (response: any) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          console.error('Empty PDF blob received');
          this.toastr.error('Received empty PDF');
          this.isLoadingPreview = false;
          return;
        }
        console.log('PDF Blob received, size:', blob.size);
        const url = URL.createObjectURL(blob);
        this.rawBlobUrl = url;
        // Add PDF parameters for cleaner view
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          url + '#toolbar=0&navpanes=0&view=FitH',
        );
        this.previewUrl = safeUrl;
        this.isLoadingPreview = false;
        console.log('Preview URL set');
      },
      error: (err) => {
        console.error('Download PO Error:', err);
        this.toastr.error('Failed to load PDF preview');
        this.isLoadingPreview = false;
      },
    });
  }

  closePreview() {
    this.showPreview = false;
    // Clean up blob URL
    if (this.rawBlobUrl) {
      URL.revokeObjectURL(this.rawBlobUrl);
      this.rawBlobUrl = null;
    }
    this.afterSave();
  }

  /** Once a save is done (preview closed, or no preview): back to the PO list. */
  private afterSave(): void {
    this.router.navigate(['/po']);
  }

  downloadPdf() {
    if (!this.rawBlobUrl) return;
    const link = document.createElement('a');
    link.href = this.rawBlobUrl;
    link.download = `PO-${this.poForm.get('poNumber')?.value || 'document'}.pdf`;
    link.click();
  }

  printPdf() {
    if (!this.rawBlobUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.rawBlobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Optional: remove iframe after small delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }

  async submitPo(
    status: 'DRAFT' | 'POSTED' = 'POSTED',
    opts: { resubmit?: boolean } = {},
  ) {
    this.poForm.markAllAsTouched();

    if (this.poForm.invalid) return;
    if (!this.selectedVendor) {
      this.toastr.warning('Select the vendor this order is for.', 'Vendor required');
      this.openVendorPicker();
      return;
    }
    this.isSaved = true;

    // ✅ FINAL VALIDATION: Ensure MRP >= CP, SP >= CP, and SP <= MRP for all items
    // (equality allowed — zero-margin items can have SP = CP = MRP)
    // Compare at 3 decimals: CP is often derived (base × (1+tax)), and float
    // noise like 84.50000001 vs 84.5 must not fail an equal-looking pair.
    for (const item of this.poItems) {
      const mrp = this.round3(Number(item.mrp || 0));
      const cp = this.round3(Number(item.costPrice || 0));
      const sp = this.round3(Number(item.sellingPrice || 0));

      console.log(`Checking Item ${item.sku}: MRP=${mrp}, CP=${cp}, SP=${sp}`);

      if (mrp < cp) {
        this.toastr.error(`Item ${item.sku} has MRP (₹${mrp}) less than Cost Price (₹${cp}). MRP should be greater than or equal to Cost Price.`, 'Pricing Validation Error');
        return;
      }

      if (sp < cp) {
        this.toastr.error(`Item ${item.sku} has Selling Price (₹${sp}) less than Cost Price (₹${cp}). Selling Price should be greater than or equal to Cost Price.`, 'Pricing Validation Error');
        return;
      }

      if (sp > mrp) {
        this.toastr.error(`Item ${item.sku} has Selling Price (₹${sp}) greater than MRP (₹${mrp}).`, 'Pricing Validation Error');
        return;
      }
    }

    // Vendor ↔ SKU mapping: only a POSTED order is checked (a draft is work
    // in progress). May remove lines, so it runs before the payload is built.
    let mappingDecisions: { map: string[]; skip: string[] } | null = null;
    if (status === 'POSTED') {
      mappingDecisions = await this.collectVendorSkuMappingDecisions();
      if (!mappingDecisions) return;
    }
    if (!this.poItems.length) {
      this.toastr.warning('Add at least one line item.', 'Nothing to submit');
      return;
    }

    const items = this.poItems.map((item) => {
      const marginPercent = Number(this.getMarginPercentage(item));
      const totalItemQty =
        Number(item.quantity || 0) + Number(item.indentQuantity || 0);
      const marginAmount = Number(
        (
          (Number(item.mrp) - Number(item.actualCostPrice)) *
          totalItemQty
        ).toFixed(2),
      );

      const mappedItem: any = {
        productVariantId: item.productVariantId,
        quantity: item.quantity,
        costPrice: Number(item.costPrice),
        actualCostPrice: Number(item.actualCostPrice),
        discountAmount: Number(item.discountAmount || 0),
        discountPercent: Number(item.discountPercent || 0),
        gstAmount: Number(item.gstAmount || 0),
        gstPercent: Number(item.gstPercent),
        cessAmount: Number(item.cessAmount || 0),
        cessRate: Number(item.cessRate),
        mrp: Number(item.mrp),
        totalPrice: Number(item.total),
        freeQuantity: item?.freeQuantity || 0,
        indentQuantity: item?.indentQuantity || 0,
        // id: item.id || null, // Don't send null ID if not valid
      };

      const isValid = this.isValidUUID(item.id);

      // This is the only place 'id' is added
      if (item.id && isValid) {
        mappedItem.id = item.id;
      }
      return mappedItem;
    });

    const payload = {
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      storeId: this.selectedWarehouse?.storeId ?? undefined,
      vendorId: this.selectedVendor,
      deliveryDate: this.poForm.value.deliveryDate
        ? new Date(this.poForm.value.deliveryDate).toISOString()
        : new Date().toISOString(),
      expiryDate: this.poForm.value.expiryDate
        ? new Date(this.poForm.value.expiryDate).toISOString()
        : new Date().toISOString(),
      indentDate: this.fromDate,
      roundOffAmount: this.roundOffAmount,
      subTotalAmount: this.summary.totalItems ? this.summary.totalBasicCost : 0, // Handle edge case
      totalGSTAmount: Number(this.summary.totalGstAmount),
      totalCessAmount: this.summary.totalCessAmount,
      totalDiscountAmount:
        this.summary.fixedDiscountAmount + this.summary.schemeDiscountAmount,
      totalItems: this.summary.totalItems,
      totalUnits: this.summary.totalQuantity,
      totalQty: this.totalReceivedQuantity,
      indentIds: this.selectedIndents || [],
      packingCharges: Number(this.poForm.value.packingCharges),
      fixedDiscountAmount: Number(this.poForm.value.fixedDiscountAmount),
      schemeDiscountAmount: Number(this.poForm.value.schemeDiscountAmount),
      freightCharges: this.freightCharges,
      totalAmount: this.summary.totalPoValue,
      costPriceIncludesGst: !!this.poForm.value.costPriceIncludesGst,
      items: items, // Add items to payload
      status: status,
      vendorSkuMapping:
        mappingDecisions && (mappingDecisions.map.length || mappingDecisions.skip.length)
          ? mappingDecisions
          : undefined,
    };

    // Final-submit confirmation. Drafts skip this — only ask before the
    // irreversible action. Uses the same ConfirmDialogService instance the
    // unsaved-changes guard uses, so the visual style matches.
    if (status === 'POSTED' && !opts.resubmit) {
      const proceed = await this.confirmDialog.confirm({
        title:
          this.mode === 'create' ? 'Submit Purchase Order?' : 'Update Purchase Order?',
        message:
          this.mode === 'create'
            ? `Once submitted, line items and pricing are locked and the order is sent to <strong>${this.selectedVendorName || 'the vendor'}</strong>. Amendments will need a revision.`
            : `The updated lines and pricing will be posted to <strong>${this.selectedVendorName || 'the vendor'}</strong>.`,
        confirmText: this.mode === 'create' ? 'Submit PO' : 'Post PO',
        cancelText: 'Cancel',
        tone: 'primary',
        icon: 'bi-arrow-up-right',
        summaryLabel: 'Grand total',
        summaryValue: '₹' + Number(this.summary.totalPoValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      });
      if (!proceed) return;
    }

    if (this.mode === 'create') {
      this.poService.createPo(payload).subscribe({
        next: (res: any) => {
          console.log('PO Create Response:', res);
          this.toastr.success('PO Created Successfully');
          // On the server now; the browser copy has done its job.
          this.discardDraft();
          this.toastMappingsApplied(res);
          if (res?.poNumber) this.poForm.patchValue({ poNumber: res.poNumber });
          // Load Preview Overlay
          const createdPoId = res?.id || res?.data?.id;

          if (createdPoId) {
            console.log('Triggering preview for PO:', createdPoId);
            this.loadPreview(createdPoId);
          } else {
            console.warn('No PO ID found in response, skipping preview.');
            // Fallback if no ID returned (should not happen normally)
            this.afterSave();
          }
        },
        error: (err) => {
          console.error('PO Create Error:', err);
          if (this.handleUnmappedSkuError(err, status)) return;
          this.toastr.error(
            typeof err?.error?.message === 'string' ? err.error.message : 'Failed to create PO',
          );
        },
      });
    } else {
      this.poService.updatePo(this.poId, payload).subscribe({
        next: (res: any) => {
          this.toastr.success('PO Updated Successfully');
          this.discardDraft();
          this.toastMappingsApplied(res);
          this.loadPreview(this.poId);
        },
        error: (err) => {
          console.error(err);
          if (this.handleUnmappedSkuError(err, status)) return;
          this.toastr.error(
            typeof err?.error?.message === 'string' ? err.error.message : 'Failed to update PO',
          );
        },
      });
    }
  }

  // ── Vendor ↔ SKU mapping ───────────────────────────────────────────────

  private scheduleVendorMappingRefresh(): void {
    this.vendorMappingRefresh$.next();
  }

  /** Lines with a usable variant id, de-duplicated, in grid order. */
  private get lineVariantIds(): string[] {
    return [
      ...new Set(
        this.poItems
          .map((i) => i?.productVariantId || i?.variantId)
          .filter((id: any): id is string => !!id),
      ),
    ];
  }

  /**
   * Ask the server which lines the selected vendor supplies and badge the
   * rest. Runs in the open modes only — in STRICT mode the searches already
   * guarantee it — and never blocks data entry: a failed call just leaves the
   * badges off, the server still checks on submit.
   */
  private refreshVendorMappingBadges(): void {
    if (!this.openVendorScope || this.isViewMode || !this.selectedVendor) {
      this.resetVendorMappingCache();
      return;
    }
    if (this.mappingCheckedVendor !== this.selectedVendor) {
      this.resetVendorMappingCache();
      this.mappingCheckedVendor = this.selectedVendor;
    }
    // Only variants not yet checked for this vendor: deleting lines or
    // editing quantities sends nothing, adding lines sends just the new ones.
    const pending = this.lineVariantIds.filter((id) => !this.mappingCheckedIds.has(id));
    if (!pending.length) return;
    pending.forEach((id) => this.mappingCheckedIds.add(id));

    this.mappingCheckPending = true;
    this.poService.previewVendorSkuMapping(this.selectedVendor, pending).subscribe({
      next: (preview) => {
        this.mappingCheckPending = false;
        this.vendorSkuMappingMode = preview.mode;
        this.applyMappingPreviewToLines(preview);
      },
      error: () => {
        this.mappingCheckPending = false;
        // Let the next refresh retry these.
        pending.forEach((id) => this.mappingCheckedIds.delete(id));
      },
    });
  }

  private resetVendorMappingCache(): void {
    this.mappingCheckedVendor = null;
    this.mappingCheckedIds.clear();
  }

  private applyMappingPreviewToLines(preview: VendorSkuMappingPreview): void {
    const byVariant = new Map<string, VendorSkuMappingItem>();
    for (const item of preview.items)
      for (const vid of item.productVariantIds) byVariant.set(vid, item);
    for (const line of this.poItems) {
      const hit = byVariant.get(line?.productVariantId || line?.variantId);
      if (!hit) continue;
      line.vendorMapped = hit.mapped;
      line.productId = line.productId || hit.productId;
      line.vendorNames = hit.vendors.map((v) => v.name);
    }
  }

  /**
   * Submit step. Resolves the per-SKU decisions to send with the PO, or null
   * when the buyer backed out. Lines the buyer chose to drop are removed here.
   */
  private async collectVendorSkuMappingDecisions(): Promise<{
    map: string[];
    skip: string[];
  } | null> {
    // A server rejection already collected the decisions — use them once.
    if (this.pendingMappingDecisions) {
      const pending = this.pendingMappingDecisions;
      this.pendingMappingDecisions = null;
      return pending;
    }

    const none = { map: [], skip: [] };
    const variantIds = this.lineVariantIds;
    if (!this.selectedVendor || !variantIds.length) return none;

    let preview: VendorSkuMappingPreview;
    try {
      preview = await firstValueFrom(
        this.poService.previewVendorSkuMapping(this.selectedVendor, variantIds),
      );
    } catch {
      // Could not check — let the save go through; the server enforces the
      // rule and its PO_UNMAPPED_SKUS answer reopens this dialog.
      return none;
    }
    this.vendorSkuMappingMode = preview.mode;
    this.applyMappingPreviewToLines(preview);

    const unmapped = preview.items.filter((i) => !i.mapped);
    if (!unmapped.length) return none;

    const result = await this.openVendorSkuMappingDialog({
      purpose: 'submit',
      mode: preview.mode,
      vendorName: preview.vendor?.name || this.selectedVendorName,
      items: unmapped,
      total: preview.total,
    });
    if (!result) return null;

    if (result.removeVariantIds.length) this.removeLinesForVariants(result.removeVariantIds);
    return { map: result.map, skip: result.skip };
  }

  private openVendorSkuMappingDialog(args: {
    purpose: 'submit' | 'review';
    mode: VendorSkuMappingMode;
    vendorName: string;
    items: VendorSkuMappingItem[];
    total: number;
  }): Promise<VendorSkuMappingDialogResult | null> {
    return new Promise((resolve) => {
      // STRICT: the mapping is owned by the mapping screen, so the only way
      // forward here is to drop the lines. Otherwise default to mapping —
      // that is what the open modes exist for.
      const defaultDecision: VendorSkuMappingDecision =
        args.mode === 'STRICT' ? 'remove' : 'map';
      this.mappingDialog = {
        purpose: args.purpose,
        mode: args.mode,
        vendorName: args.vendorName,
        rows: args.items.map((i) => ({ ...i, decision: defaultDecision })),
        total: args.total,
        busy: false,
        resolve,
      };
    });
  }

  /** Decisions offered per row, by mode. */
  get mappingDecisionOptions(): VendorSkuMappingDecision[] {
    switch (this.mappingDialog?.mode) {
      case 'PROMPT':
        return ['map', 'skip', 'remove'];
      case 'REQUIRED':
        return ['map', 'remove'];
      default:
        return [];
    }
  }

  get mappingDialogCounts(): { map: number; skip: number; remove: number } {
    const c = { map: 0, skip: 0, remove: 0 };
    for (const r of this.mappingDialog?.rows ?? []) c[r.decision]++;
    return c;
  }

  get mappingDialogConfirmLabel(): string {
    const d = this.mappingDialog;
    if (!d) return '';
    const c = this.mappingDialogCounts;
    const parts: string[] = [];
    if (c.map) parts.push(`Map ${c.map}`);
    if (c.remove) parts.push(`remove ${c.remove}`);
    const action = parts.length ? parts.join(', ') : 'Continue without mapping';
    if (d.purpose === 'review') return parts.length ? action : 'Done';
    const tail = parts.length ? ' & continue' : '';
    return action + tail;
  }

  /** Nothing left on the order once the removals are applied. */
  get mappingDialogRemovesEverything(): boolean {
    const d = this.mappingDialog;
    if (!d) return false;
    const dropping = new Set(
      d.rows.filter((r) => r.decision === 'remove').flatMap((r) => r.productVariantIds),
    );
    return this.poItems.every((i) => dropping.has(i?.productVariantId || i?.variantId));
  }

  setMappingDecision(row: VendorSkuMappingDialogRow, decision: VendorSkuMappingDecision): void {
    if (!this.mappingDialog || this.mappingDialog.busy) return;
    if (decision !== 'remove' && !this.mappingDecisionOptions.includes(decision)) return;
    row.decision = decision;
  }

  setAllMappingDecisions(decision: VendorSkuMappingDecision): void {
    if (!this.mappingDialog || this.mappingDialog.busy) return;
    if (decision !== 'remove' && !this.mappingDecisionOptions.includes(decision)) return;
    for (const r of this.mappingDialog.rows) r.decision = decision;
  }

  cancelMappingDialog(): void {
    if (!this.mappingDialog || this.mappingDialog.busy) return;
    const d = this.mappingDialog;
    this.mappingDialog = null;
    d.resolve(null);
  }

  async confirmMappingDialog(): Promise<void> {
    const d = this.mappingDialog;
    if (!d || d.busy) return;
    const result: VendorSkuMappingDialogResult = {
      map: d.rows.filter((r) => r.decision === 'map').map((r) => r.productId),
      skip: d.rows.filter((r) => r.decision === 'skip').map((r) => r.productId),
      removeVariantIds: d.rows
        .filter((r) => r.decision === 'remove')
        .flatMap((r) => r.productVariantIds),
    };

    if (d.purpose === 'submit') {
      this.mappingDialog = null;
      d.resolve(result);
      return;
    }

    // "Map now": apply the mappings immediately, drop the removed lines.
    d.busy = true;
    try {
      if (result.map.length) {
        const res = await firstValueFrom(
          this.poService.applyVendorSkuMapping(this.selectedVendor, result.map),
        );
        this.toastr.success(
          `${res.mapped.length} SKU(s) mapped to ${res.vendor?.name || d.vendorName}.`,
          'Vendor SKU mapping',
        );
      }
      if (result.removeVariantIds.length) this.removeLinesForVariants(result.removeVariantIds);
      this.mappingDialog = null;
      // Mappings just changed on the server — re-check every line.
      this.resetVendorMappingCache();
      this.scheduleVendorMappingRefresh();
      d.resolve(result);
    } catch (err: any) {
      d.busy = false;
      this.toastr.error(
        typeof err?.error?.message === 'string' ? err.error.message : 'Could not map the SKUs.',
      );
    }
  }

  /** Banner action: review and fix the unmapped lines before submitting. */
  async reviewVendorSkuMapping(): Promise<void> {
    if (this.isViewMode || !this.selectedVendor) return;
    const variantIds = this.lineVariantIds;
    if (!variantIds.length) return;
    let preview: VendorSkuMappingPreview;
    try {
      preview = await firstValueFrom(
        this.poService.previewVendorSkuMapping(this.selectedVendor, variantIds),
      );
    } catch {
      this.toastr.error('Could not check the vendor mapping right now.');
      return;
    }
    this.vendorSkuMappingMode = preview.mode;
    this.applyMappingPreviewToLines(preview);
    const unmapped = preview.items.filter((i) => !i.mapped);
    if (!unmapped.length) {
      this.toastr.success(`Every line is mapped to ${preview.vendor?.name || this.selectedVendorName}.`);
      return;
    }
    await this.openVendorSkuMappingDialog({
      purpose: 'review',
      mode: preview.mode,
      vendorName: preview.vendor?.name || this.selectedVendorName,
      items: unmapped,
      total: preview.total,
    });
  }

  private removeLinesForVariants(variantIds: string[]): void {
    const drop = new Set(variantIds);
    const removed = this.poItems.filter((i) => drop.has(i?.productVariantId || i?.variantId));
    if (!removed.length) return;
    for (const item of removed) this.selectedItems.delete(item);
    this.poItems = this.poItems.filter((i) => !removed.includes(i));
    this.updateSummary();
    this.toastr.info(`${removed.length} line(s) removed from the order.`);
  }

  /**
   * The server is the real guard: when it answers PO_UNMAPPED_SKUS (the
   * pre-submit check failed, or the mapping changed meanwhile) the same dialog
   * is shown from its payload and the save is retried with the decisions.
   */
  private handleUnmappedSkuError(err: any, status: 'DRAFT' | 'POSTED'): boolean {
    const body = err?.error;
    if (body?.code !== 'PO_UNMAPPED_SKUS' || !Array.isArray(body.unmapped)) return false;
    void this.recoverFromUnmappedSkuError(body, status);
    return true;
  }

  private async recoverFromUnmappedSkuError(body: any, status: 'DRAFT' | 'POSTED'): Promise<void> {
    const mode: VendorSkuMappingMode =
      body.mode === 'PROMPT' || body.mode === 'REQUIRED' ? body.mode : 'STRICT';
    this.vendorSkuMappingMode = mode;
    const items: VendorSkuMappingItem[] = body.unmapped;
    for (const line of this.poItems) {
      const hit = items.find((i) =>
        i.productVariantIds?.includes(line?.productVariantId || line?.variantId),
      );
      if (hit) {
        line.vendorMapped = false;
        line.vendorNames = (hit.vendors || []).map((v) => v.name);
      }
    }
    const result = await this.openVendorSkuMappingDialog({
      purpose: 'submit',
      mode,
      vendorName: body.vendor?.name || this.selectedVendorName,
      items,
      total: this.poItems.length,
    });
    if (!result) return;
    if (result.removeVariantIds.length) this.removeLinesForVariants(result.removeVariantIds);
    if (!this.poItems.length) {
      this.toastr.info('All lines were removed; nothing left to submit.');
      return;
    }
    this.pendingMappingDecisions = { map: result.map, skip: result.skip };
    await this.submitPo(status, { resubmit: true });
  }

  private toastMappingsApplied(res: any): void {
    const n = Number(res?.vendorSkuMappingsApplied || 0);
    if (n > 0)
      this.toastr.success(
        `${n} SKU(s) mapped to ${this.selectedVendorName || 'the vendor'}.`,
        'Vendor SKU mapping',
      );
  }

  // for ROI
  getMarginPercentage(item: any): string {
    if (item && item.marginPercentage !== undefined && item.marginPercentage !== null) {
      console.log(`[Margin Info] Using stored margin for ${item.sku || 'item'}: ${item.marginPercentage}%`);
      return Number(item.marginPercentage).toFixed(2);
    }
    if (!item || item.actualCostPrice <= 0) return '0.00';
    const margin =
      ((item.mrp - item.actualCostPrice) / item.actualCostPrice) * 100;
    console.log(`[Margin Info] Calculated ROI margin for ${item.sku || 'item'}: ${margin.toFixed(2)}%`);
    return margin.toFixed(2); // two decimal places
  }

  customSearchFn(term: string, item: any): boolean {
    term = term.toLowerCase();
    return (
      item?.longDescription?.toLowerCase()?.includes(term) ||
      item?.skuCode?.toLowerCase()?.includes(term)
    );
  }

  onProductSelected(product: any) {
    this.selectedProduct = product;
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.MRP = this.selectedVariant?.pricing?.[0]?.mrp;
    this.warnIfUnpriced(product, this.selectedVariant);
    // A SKU can be supplied by several vendors (Vendor SKU Mapping), and the
    // search above was already scoped to the vendor the user chose. Only fall
    // back to the product's primary vendor when no vendor is selected yet —
    // overwriting the user's choice would silently re-address the PO.
    if (!this.selectedVendor && product.vendorsId) {
      this.selectedVendor = product.vendorsId;
      this.onVendorChange();
    }
    console.log(`[Product Select] Product: ${product.longDescription}, Margin: ${product.marginPercentage}%, GST: ${product.purchaseGstRate?.value}%`);

    this.populatePriceOptions(product);

    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    });
  }

  /** A SKU may be created with only a barcode; say so instead of prefilling ₹0. */
  private warnIfUnpriced(product: any, variant: any) {
    if (!product || hasPurchasePrice(variant)) return;
    this.toastr.warning(
      priceNotSetMessage(product.sku, product.longDescription),
      'Price required',
    );
  }

  onSearchResultSelected(product: any) {
    if (!product) return;

    this.selectedProduct = product;
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.MRP = this.selectedVariant?.pricing?.[0]?.mrp;
    this.warnIfUnpriced(product, this.selectedVariant);
    // A SKU can be supplied by several vendors (Vendor SKU Mapping), and the
    // search above was already scoped to the vendor the user chose. Only fall
    // back to the product's primary vendor when no vendor is selected yet —
    // overwriting the user's choice would silently re-address the PO.
    if (!this.selectedVendor && product.vendorsId) {
      this.selectedVendor = product.vendorsId;
      this.onVendorChange();
    }
    console.log(`[Search Select] Product: ${product.longDescription}, Margin: ${product.marginPercentage}%, GST: ${product.purchaseGstRate?.value}%`);

    this.populatePriceOptions(product);

    // Focus on quantity input
    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    }, 100);

    // Show success message
    this.toastr.success(
      `Product found: ${product.longDescription}`,
      'Product Selected',
    );
  }

  populatePriceOptions(product: any) {
    if (!product) return;
    const prices: number[] = [];
    const mrps: number[] = [];

    const variants = product.variants || [];
    variants.forEach((v: any) => {
      if (v.pricing) {
        v.pricing.forEach((p: any) => {
          if (p.costPrice) prices.push(+p.costPrice);
          if (p.mrp) mrps.push(+p.mrp);
        });
      }
    });

    // Keep the API order — most recently updated price first — so the top
    // option is the one the line defaults to (Set keeps first occurrence).
    this.allCostPrices = [...new Set(prices)];
    this.allMRPs = [...new Set(mrps)];
  }

  confirmAddToTable() {
    if (!this.selectedProduct) return;
    if (!(+this.selectedQuantity > 0)) {
      this.toastr.warning('Enter a quantity above 0.', 'Quantity needed');
      return;
    }
    // A SKU whose only price row has cost 0 used to make "Add line" do
    // nothing. Say why and open New pricing so a cost can be entered.
    if (!(+this.enteredCostPrice > 0)) {
      this.toastr.warning(
        `${this.selectedVariant?.sku || this.selectedProduct?.sku || 'This SKU'} has no cost price. Enter one in New pricing.`,
        'Cost price needed',
      );
      this.openNewPricingForm();
      // Keep the known MRP so "cost from MRP (margin %)" works straight away.
      if (+this.MRP > 0) this.newPricing.mrp = +this.MRP;
      return;
    }

    // One SKU may appear on several lines as long as the cost price differs
    // (the same PO can order old-price and new-price stock). Only an exact
    // SKU + cost price repeat is stopped — warn and jump to that row so the
    // user edits its quantity instead of creating an identical line.
    const dupIndex = this.poItems.findIndex(
      (item) =>
        (item.productVariantId === this.selectedVariant?.id ||
          (!!item.sku && item.sku === (this.selectedVariant?.sku || this.selectedProduct?.sku))) &&
        +item.costPrice === +this.enteredCostPrice,
    );
    if (dupIndex !== -1) {
      this.toastr.warning(
        `${this.selectedVariant?.sku || this.selectedProduct?.sku || 'This item'} is already in the list at this cost price.`,
        'Item already added',
      );
      this.selectedProduct = null;
      this.selectedVariant = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.MRP = 0;
      this.allCostPrices = [];
      this.allMRPs = [];
      setTimeout(() => {
        const el = document.getElementById('po-qty-' + dupIndex) as HTMLInputElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
          el.select();
        }
      }, 100);
      return;
    }

    const existing = this.selectedVariant.pricing.find(
      (p: any) => +p.costPrice === +this.enteredCostPrice,
    );

    const proceed = (pricingList: any[]) => {
      const gstRate = parseFloat(
        this.selectedProduct?.purchaseGstRate?.value || '0',
      );
      const costPrice = +this.enteredCostPrice;
      const match = pricingList.find((p: any) => +p.costPrice === +costPrice) || pricingList[0];
      // basicCostPrice is the stored paired value for this costPrice row.
      const matchedBasic = Number(match?.basePrice || 0);
      const basicCostPrice =
        matchedBasic > 0 ? matchedBasic : +(costPrice / (1 + gstRate / 100)).toFixed(4);
      const breakup = this.computeLineTaxBreakup(
        basicCostPrice,
        costPrice,
        this.selectedQuantity,
        gstRate,
        0,
      );
      const actualCost = breakup.actualCostPrice;
      const basicCostTotal = breakup.basicCostTotal;
      const gstAmount = breakup.gstAmount;
      const total = breakup.lineGross;

      this.poItems.push({
        pricing: pricingList,
        productVariantId: this.selectedVariant.id,
        variantName: this.selectedVariant.variantName,
        quantity: this.selectedQuantity,
        costPrice,
        actualCostPrice: actualCost,
        mrp: Number(match?.mrp || 0),
        sellingPrice: Number(match?.sellingPrice || 0),
        gstPercent: gstRate,
        gstAmount,
        total,
        basicCostTotal,
        innerPackaging: this.selectedProduct?.innerPackaging || 1,
        sku: this.selectedVariant.sku || this.selectedProduct.sku || '',

        // ✅ Store metadata for New Pricing modal support from SKU clicks
        productData: this.selectedProduct,
        variantData: this.selectedVariant,
        marginPercentage: this.selectedProduct?.marginPercentage || 0,
      });

      // Reset full selection state — the form has multiple bindings populated
      // by onSearchResultSelected / onProductSelected and they all need to
      // clear for the search row to return to its initial empty state.
      this.selectedProduct = null;
      this.selectedVariant = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.MRP = 0;
      this.allCostPrices = [];
      this.allMRPs = [];
      this.updateSummary();
    };

    if (existing) {
      proceed(this.selectedVariant.pricing);
    } else {
      const last = this.selectedVariant.pricing?.[0];
      const body = {
        ...last,
        costPrice: +this.enteredCostPrice,
        basePrice: +this.enteredCostPrice,
      };
      delete body.id;
      delete body.createdAt;
      delete body.updatedAt;
      body.mrp = Number(body.mrp);
      body.sellingPrice = Number(body.sellingPrice);
      this.productService.addVariantPricing(body).subscribe((res: any) => {
        // append new pricing to local array
        this.selectedVariant.pricing.unshift(body);
        proceed(this.selectedVariant.pricing);
      });
    }
  }
  cancel() {
    this.router.navigate(['/po']);
  }

  formatDateToDDMMYYYY(date: Date | null | undefined): string {
    // Handle cases where the input date might be null, undefined, or an invalid Date object
    if (!date || isNaN(date.getTime())) {
      return ''; // Or throw an error, or return a default string like 'N/A'
    }

    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Month is 0-indexed, so add 1
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  toIsoStringFromDDMMYYYY(dateStr: string): string {
    if (!dateStr) return '';

    // Split "dd-MM-yyyy" into parts
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';

    const [dd, mm, yyyy] = parts;

    // Construct ISO date string "yyyy-MM-dd"
    const isoFormatted = `${yyyy}-${mm}-${dd}`;

    const date = new Date(isoFormatted + 'T00:00:00.000Z');
    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }
  toIsoStringFromYYYYMMDD(dateStr: string): string {
    if (!dateStr) return '';

    // Split "yyyy-MM-dd" into parts
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';

    const [yyyy, mm, dd] = parts;

    // Basic validation for numeric parts and length
    if (
      isNaN(Number(yyyy)) ||
      isNaN(Number(mm)) ||
      isNaN(Number(dd)) ||
      yyyy.length !== 4 ||
      mm.length !== 2 ||
      dd.length !== 2
    ) {
      return '';
    }

    const isoFormatted = `${yyyy}-${mm}-${dd}`;

    const date = new Date(isoFormatted + 'T00:00:00.000Z');

    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }

  saveAsDraft(): void {
    this.isSaved = true;
    this.submitPo('DRAFT');
  }

  filterExistingItems() {
    // If no items in table, nothing to filter
    if (this.poItems.length === 0) {
      return;
    }

    // Store original count for logging
    const originalCount = this.poItems.length;

    // Filter items based on current filter selections
    this.poItems = this.poItems.filter((item) => {
      let keepItem = true;

      if (this.selectedManufacturers && this.selectedManufacturers.length > 0) {
        let manufacturerMatch = false;

        if (
          item.manufacturerId &&
          this.selectedManufacturers.includes(item.manufacturerId)
        ) {
          manufacturerMatch = true;
        }

        if (
          !manufacturerMatch &&
          item.manufacturerIds &&
          item.manufacturerIds.length > 0
        ) {
          manufacturerMatch = item.manufacturerIds.some((id: string) =>
            this.selectedManufacturers.includes(id),
          );
        }

        if (!manufacturerMatch) {
          keepItem = false;
        }
      }

      // Check brand filter (only if item passed manufacturer check)
      if (keepItem && this.selectedBrands && this.selectedBrands.length > 0) {
        let brandMatch = false;

        // Check single brandId
        if (item.brandId && this.selectedBrands.includes(item.brandId)) {
          brandMatch = true;
        }

        // Check brandIds array (for products with multiple brands)
        if (!brandMatch && item.brandIds && item.brandIds.length > 0) {
          brandMatch = item.brandIds.some((id: string) =>
            this.selectedBrands.includes(id),
          );
        }

        if (!brandMatch) {
          console.log(`Removing item ${item.variantName} - brand mismatch`, {
            itemBrandId: item.brandId,
            itemBrandIds: item.brandIds,
            itemBrandName: item.brandName,
            selectedBrands: this.selectedBrands,
          });
          keepItem = false;
        } else {
          console.log(`Brand match found for ${item.variantName}`, {
            itemBrandId: item.brandId,
            itemBrandIds: item.brandIds,
            selectedBrands: this.selectedBrands,
          });
        }
      }

      // Check department filter (only if item passed previous checks)
      if (
        keepItem &&
        this.selectedDepartment &&
        this.selectedDepartment !== ''
      ) {
        if (
          !item.departmentId ||
          item.departmentId !== this.selectedDepartment
        ) {
          console.log(
            `Removing item ${item.variantName} - department mismatch`,
            {
              itemDepartmentId: item.departmentId,
              selectedDepartment: this.selectedDepartment,
            },
          );
          keepItem = false;
        }
      }

      // Check category filter (only if item passed previous checks)
      if (keepItem && this.selectedCategory && this.selectedCategory !== '') {
        if (!item.categoryId || item.categoryId !== this.selectedCategory) {
          keepItem = false;
        }
      }

      // Check subcategory filter (only if item passed previous checks)
      if (
        keepItem &&
        this.selectedSubCategories &&
        this.selectedSubCategories.length > 0
      ) {
        if (
          !item.subCategoryId ||
          !this.selectedSubCategories.includes(item.subCategoryId)
        ) {
          keepItem = false;
        }
      }

      if (keepItem) {
        console.log(`✅ Keeping item ${item.variantName}`);
      } else {
        console.log(`❌ Removing item ${item.variantName}`);
      }

      return keepItem;
    });

    // Log filtering results
    const filteredCount = this.poItems.length;
    const removedCount = originalCount - filteredCount;

    console.log(
      `Filtering complete: ${removedCount} items removed, ${filteredCount} items remaining`,
    );

    // Update summary after filtering
    this.updateSummary();

    // Show user feedback if items were removed
    if (removedCount > 0) {
      this.toastr.info(
        `${removedCount} item(s) removed due to filter changes`,
        'Items Filtered',
      );
    }
  }

  isValidUUID(uuid: any): boolean {
    // Note the change in the third and fourth groups from [1-5] and [89ab] to [0-9a-f]
    const permissiveUuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return typeof uuid === 'string' && permissiveUuidRegex.test(uuid);
  }


  onSearchTypeChange() {
    this.searchResults = [];
    this.eanSearchResults = [];
    // Clear current selection when search type changes
    if (this.selectedProduct) {
      this.selectedProduct = null;
    }
  }

  get totalReceivedQuantity(): number {
    return (
      this.poItems?.reduce(
        (sum, item) =>
          sum + Number(item.quantity || 0) + Number(item.indentQuantity || 0) + Number(item.freeQuantity || 0),
        0,
      ) || 0
    );
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isSaved) {
      return true;
    }
    if (this.mode === 'edit') {
      const leave = await this.confirmDialog.confirm({
        title: 'Leave Edit Page?',
        message:
          'Are you sure you want to leave <strong>Edit Purchase Order</strong> page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
      // Leaving on purpose discards the browser copy too. Not when this is
      // the sign-out path (no token by now): that copy is the user's way back.
      if (leave && this.authService.isLoggedIn()) this.discardDraft();
      return leave;
    }

    // check for unsaved form values or items
    if (this.poForm?.dirty || (this.poItems && this.poItems.length > 0)) {
      // using your confirm dialog service (same one from GRN)
      const leave = await this.confirmDialog.confirm({
        title: 'Discard changes?',
        message:
          'You have unsaved changes in the  <strong>Purchase Order</strong>. Are you sure you want to leave this page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
      if (leave && this.authService.isLoggedIn()) this.discardDraft();
      return leave;
    }

    return true;
  }

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // Same protection as the GRN form (a 140-line GRN was lost when the
  // session ran out before Save Draft): the form copies itself to the
  // browser every few seconds (FormDraftStore) and offers it back on reopen.
  private draftStore: FormDraftStore<PoDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank form
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Saved to the server, or discarded on purpose: stop copying. */
  private draftClosed = false;

  private initDraft(poId: string | null): void {
    if (this.mode === 'view') return;
    this.draftStore = new FormDraftStore<PoDraftSnapshot>([
      'po',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      poId ?? 'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private hasDraftContent(d: Partial<PoDraftSnapshot> | null | undefined): boolean {
    return !!(d?.poItems?.length || d?.selectedVendor || d?.selectedIndents?.length);
  }

  private draftSnapshot(): PoDraftSnapshot {
    const f = this.poForm.getRawValue();
    return {
      selectedVendor: this.selectedVendor ?? null,
      vendor: this.vendors.find((v: any) => v.id === this.selectedVendor) ?? null,
      deliveryDate: f.deliveryDate,
      expiryDate: f.expiryDate,
      packingCharges: f.packingCharges,
      schemeDiscountAmount: f.schemeDiscountAmount,
      fixedDiscountAmount: f.fixedDiscountAmount,
      costPriceIncludesGst: !!f.costPriceIncludesGst,
      fromDate: this.fromDate,
      selectedIndents: this.selectedIndents,
      indentOptions: this.indentOptions.filter((o) =>
        this.selectedIndents?.includes(o.id),
      ),
      selectedManufacturers: this.selectedManufacturers,
      selectedBrands: this.selectedBrands,
      selectedDepartment: this.selectedDepartment,
      selectedCategory: this.selectedCategory,
      selectedSubCategories: this.selectedSubCategories,
      poItems: this.poItems,
    };
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.draftSnapshot();
    // An emptied form leaves any earlier copy alone ("Not now" kept it).
    if (this.hasDraftContent(snapshot)) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /**
   * Offer a copy left by an earlier visit. For an existing PO a copy older
   * than the version on the server is stale and silently dropped.
   */
  private async offerDraftRestore(serverUpdatedAt?: string): Promise<void> {
    const stored = this.draftStore?.load();
    if (!stored || !this.hasDraftContent(stored.data)) {
      this.draftReady = true;
      return;
    }
    if (serverUpdatedAt && stored.savedAt <= Date.parse(serverUpdatedAt)) {
      this.draftStore!.clear();
      this.draftReady = true;
      return;
    }

    const d = stored.data;
    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const lines = d.poItems?.length ?? 0;
    const details = [
      d.vendor?.name ? `vendor <strong>${escapeHtml(d.vendor.name)}</strong>` : '',
      `<strong>${lines}</strong> line${lines === 1 ? '' : 's'}`,
    ].filter(Boolean);
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved Purchase Order?',
      message:
        `This PO was not saved. A copy from ${when} is still on this computer: ${details.join(', ')}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you start entering a PO here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(d);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: PoDraftSnapshot): void {
    // Make sure the pickers can show the restored values.
    if (d.vendor?.id && !this.vendors.some((v: any) => v.id === d.vendor.id)) {
      this.vendors = [...this.vendors, d.vendor];
    }
    const knownIndents = new Set(this.indentOptions.map((o) => o.id));
    const missingIndents = (d.indentOptions ?? []).filter((o) => !knownIndents.has(o.id));
    if (missingIndents.length) {
      this.indentOptions = [...this.indentOptions, ...missingIndents];
    }

    // Not applyVendor()/onVendorChange(): those clear the lines being restored.
    this.selectedVendor = d.selectedVendor;
    this.vendorSelect?.writeValue(this.selectedVendor || null);
    this.poForm.patchValue(
      {
        deliveryDate: d.deliveryDate,
        expiryDate: d.expiryDate,
        packingCharges: d.packingCharges,
        schemeDiscountAmount: d.schemeDiscountAmount,
        fixedDiscountAmount: d.fixedDiscountAmount,
        costPriceIncludesGst: !!d.costPriceIncludesGst,
      },
      // The copy is restored as it was; the date watchers would re-toast.
      { emitEvent: false },
    );
    this.poForm.markAsDirty();
    this.fromDate = d.fromDate ?? '';
    this.selectedIndents = d.selectedIndents ?? [];
    this.selectedManufacturers = d.selectedManufacturers ?? [];
    this.selectedBrands = d.selectedBrands ?? [];
    this.selectedDepartment = d.selectedDepartment ?? '';
    this.selectedCategory = d.selectedCategory ?? null;
    this.selectedSubCategories = d.selectedSubCategories ?? [];
    this.poItems = d.poItems ?? [];

    // Option lists behind the filter pickers, as loadPurchaseOrder loads them.
    if (this.selectedVendor) {
      this.vendorService
        .getManufacturerDetails(this.selectedVendor)
        .subscribe((res: any) => {
          this.manufacturers = res || [];
          this.selectedManufacturers = this.keepKnown(
            this.selectedManufacturers,
            this.manufacturers,
          );
          this.brands = this.manufacturers
            .filter((m) => this.selectedManufacturers.includes(m.id))
            .flatMap((m) => m.brands || []);
          this.selectedBrands = this.keepKnown(this.selectedBrands, this.brands);
        });
    }
    const departmentId = ([] as string[]).concat(this.selectedDepartment || [])[0];
    if (departmentId) {
      this.poService
        .getCategoriesByDepartment(departmentId)
        .subscribe((res: any) => (this.categories = res?.data || []));
    }
    if (this.selectedCategory) {
      this.poService
        .getSubCategoriesByCategory(this.selectedCategory)
        .subscribe((res: any) => {
          this.subCategories = res?.data || [];
          this.selectedSubCategories = this.keepKnown(
            this.selectedSubCategories,
            this.subCategories,
          );
        });
    }
    if (this.fromDate) this.loadAllIndentsForDate(this.fromDate);

    // Vendor-mapping badges are re-checked for the restored lines.
    this.resetVendorMappingCache();
    this.updateSummary();
    this.toastr.success(
      `${this.poItems.length} line(s) restored. Save draft to keep them on the server.`,
      'PO restored',
    );
  }
  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  // ... existing class properties and methods


  selectAllIndents(): void {
    if (this.isViewMode || !this.indentOptions.length) {
      return;
    }

    // Get all 'id's from the indentOptions array
    const allIndentIds = this.indentOptions.map((indent) => indent.id);

    // Assign them to the selectedIndents array
    this.selectedIndents = allIndentIds;

    this.toastr.info(`${allIndentIds.length} Indents selected.`);
  }

  // --- NEW PRICING MODAL METHODS ---

  openNewPricingForm(item?: any): void {
    this.editingItem = item || null; // ✅ Store reference
    if (item) {
      // Case 1: Item clicked from the PO table
      this.selectedProduct = item.productData;
      this.selectedVariant = item.variantData;
    } else if (!this.selectedProduct) {
      // Case 2: Clicked from the manual product add section but no product selected
      this.toastr.warning('Please select a product first.');
      return;
    }

    // Clear previous errors
    this.newPricingValidationError = null;

    // Initialize with zeros
    this.newPricing = {
      basePrice: 0,
      costPrice: 0,
      sellingPrice: 0,
      mrp: 0,
    };

    this.sellingAtMrpAsked = false;
    this.sellingFollowsMrp = false;

    // Margin-driven CP starts OFF every time; the % is pre-filled from the
    // product master so enabling it needs no lookup.
    this.useMarginForCp = false;
    this.pricingMarginPercent =
      Number(this.selectedProduct?.marginPercentage) || 0;

    this.isPricingFormVisible = true;
  }

  onMarginSettingChange(): void {
    if (this.useMarginForCp && (Number(this.newPricing.mrp) || 0) > 0) {
      this.calculateNewPricing('mrp');
    } else {
      this.validatePricing();
    }
  }

  getCurrentProductGstRate(): number {
    const gstPercent = parseFloat(
      this.selectedProduct?.purchaseGstRate?.value || '0',
    );
    console.log(`[Tax Info] Selected Product GST: ${gstPercent}%`);
    // Return the rate as a factor (e.g., 5% becomes 0.05)
    return gstPercent / 100;
  }

  calculateNewPricing(changedField: 'base' | 'cost' | 'mrp' | 'sellingPrice'): void {
    // Margin-driven CP is opt-in via the popup checkbox; when it is off the
    // margin is treated as 0 so MRP and CP stay independently editable.
    const marginPercent = this.useMarginForCp
      ? Number(this.pricingMarginPercent) || 0
      : 0;
    const gstRateFactor = this.getCurrentProductGstRate();
    const cessRate = parseFloat(this.selectedProduct?.cessRate || '0') / 100;
    const taxFactor = gstRateFactor + cessRate;

    console.log(`[PO Pricing Calc] Trigger: ${changedField}, Margin: ${marginPercent}%, Tax Factor: ${taxFactor.toFixed(4)}`);

    let basePrice = Number(this.newPricing.basePrice) || 0;
    let costPrice = Number(this.newPricing.costPrice) || 0;
    let mrp = Number(this.newPricing.mrp) || 0;

    // MASTER-DRIVER LOGIC: 
    // 1. If MRP is edited, it ALWAYS calculates Cost Price and Base Price.
    // 2. If Margin is 0, we skip the CP/MRP cross-calculation to allow independent entry.
    // 3. If Cost/Base Price is edited, it only updates MRP if MRP is 0 OR if CP > MRP.

    if (changedField === 'mrp') {
      // From MRP: CP = MRP * (1 - Margin%), Base = CP / (1 + Tax).
      // With the margin flag off, MRP entry leaves CP untouched.
      if (marginPercent > 0) {
        costPrice = mrp * (1 - marginPercent / 100);
        basePrice = costPrice / (1 + taxFactor);
      }
    } else if (changedField === 'cost') {
      // From CP: Base = CP / (1 + Tax)
      basePrice = costPrice / (1 + taxFactor);

      // Update MRP only if it's currently empty/zero OR if we just exceeded it
      if (marginPercent > 0 && (mrp === 0 || costPrice > mrp)) {
        mrp = costPrice / (1 - marginPercent / 100);
      }
    } else if (changedField === 'base') {
      // From Base Price: CP = Base * (1 + Tax)
      costPrice = basePrice * (1 + taxFactor);

      // Update MRP only if it's currently empty/zero OR if we just exceeded it
      if (marginPercent > 0 && (mrp === 0 || costPrice > mrp)) {
        mrp = costPrice / (1 - marginPercent / 100);
      }
    }

    this.newPricing.basePrice = parseFloat(basePrice.toFixed(4));
    this.newPricing.costPrice = parseFloat(costPrice.toFixed(4));
    this.newPricing.mrp = parseFloat(mrp.toFixed(4));

    // Selling Price is entered manually, unless the user agreed to sell at
    // MRP (offerSellingAtMrp) — then it follows MRP until SP is typed.
    if (changedField === 'sellingPrice') {
      this.sellingFollowsMrp = false;
    } else if (this.sellingFollowsMrp) {
      this.newPricing.sellingPrice = this.newPricing.mrp;
    }

    this.validatePricing();
  }

  /** MRP field (change): offer SP = MRP while the selling price is still empty. */
  async offerSellingAtMrp(): Promise<void> {
    const mrp = Number(this.newPricing.mrp) || 0;
    if (this.sellingAtMrpAsked || mrp <= 0 || Number(this.newPricing.sellingPrice) > 0) return;
    this.sellingAtMrpAsked = true;
    const yes = await this.confirmDialog.confirm({
      title: 'Selling price same as MRP?',
      message: `Set the selling price to the MRP, ₹${mrp.toFixed(2)}? Choose No to enter a different selling price.`,
      confirmText: 'Yes, sell at MRP',
      cancelText: 'No',
      tone: 'neutral',
      icon: 'bi-tag',
    });
    if (!yes || !this.isPricingFormVisible) return;
    this.sellingFollowsMrp = true;
    this.newPricing.sellingPrice = Number(this.newPricing.mrp) || 0;
    this.validatePricing();
  }

  validatePricing(): boolean {
    this.newPricingValidationError = this.getPricingValidationError();
    return this.newPricingValidationError === null;
  }

  /**
   * Selling at cost is allowed, but it is nearly always worth a second look,
   * so the popup flags it without blocking the save.
   */
  get isZeroMarginPricing(): boolean {
    const cp = Number(this.newPricing?.costPrice);
    const sp = Number(this.newPricing?.sellingPrice);
    return cp > 0 && sp > 0 && cp === sp;
  }

  /**
   * Margin hints shown under the price fields, each only once both inputs it
   * needs are positive.
   *   Vendor Margin % (under MRP)            = (MRP − CP) / MRP × 100
   *   Customer Profit Margin % (under SP)    = (SP − CP) / SP × 100
   */
  get vendorMarginPercent(): number | null {
    const cp = Number(this.newPricing?.costPrice) || 0;
    const mrp = Number(this.newPricing?.mrp) || 0;
    if (cp <= 0 || mrp <= 0) return null;
    return ((mrp - cp) / mrp) * 100;
  }

  get customerMarginPercent(): number | null {
    const cp = Number(this.newPricing?.costPrice) || 0;
    const sp = Number(this.newPricing?.sellingPrice) || 0;
    if (cp <= 0 || sp <= 0) return null;
    return ((sp - cp) / sp) * 100;
  }

  /** Round to 3 decimals so derived prices (base × (1+tax)) compare cleanly. */
  private round3(v: number): number {
    return Math.round(v * 1000) / 1000;
  }

  getPricingValidationError(): string | null {
    if (
      !this.newPricing.basePrice ||
      !this.newPricing.costPrice ||
      !this.newPricing.sellingPrice ||
      !this.newPricing.mrp ||
      this.newPricing.basePrice <= 0 ||
      this.newPricing.costPrice <= 0 ||
      this.newPricing.sellingPrice <= 0 ||
      this.newPricing.mrp <= 0
    ) {
      return 'All pricing fields must be positive values.';
    }

    const basePrice = this.round3(Number(this.newPricing.basePrice));
    const costPrice = this.round3(Number(this.newPricing.costPrice));
    const sellingPrice = this.round3(Number(this.newPricing.sellingPrice));
    const mrp = this.round3(Number(this.newPricing.mrp));

    if (basePrice > costPrice) {
      return 'Base Price cannot be greater than Cost Price.';
    }

    if (mrp < costPrice) {
      return 'MRP should always be greater than or equal to Cost Price.';
    }

    if (sellingPrice < costPrice) {
      return 'Selling Price should also be greater than or equal to Cost Price.';
    }

    if (sellingPrice > mrp) {
      return 'Selling Price can be same as MRP but not greater.';
    }

    return null;
  }

  /**
   * @param addLine From the add bar only: after saving, add the selected
   * product as a line at the new price (same checks as "Add line").
   */
  saveNewPricing(addLine = false): void {
    if (!this.selectedVariant || !this.selectedProduct) return;
    if (!this.validatePricing()) return;

    const body = {
      variantId: this.selectedVariant.id,
      mrp: Number(this.newPricing.mrp),
      costPrice: Number(this.newPricing.costPrice),
      basePrice: Number(this.newPricing.basePrice),
      sellingPrice: Number(this.newPricing.sellingPrice),
      validFrom: new Date().toISOString(),
    };

    this.productService.addVariantPricing(body).subscribe({
      next: (res: any) => {
        this.toastr.success('New pricing added successfully!', 'Success');

        if (!this.selectedVariant.pricing) {
          this.selectedVariant.pricing = [];
        }
        this.selectedVariant.pricing.unshift(res);

        this.enteredCostPrice = res.costPrice;
        this.MRP = res.mrp;

        this.populatePriceOptions(this.selectedProduct);

        // 3. Update the specific item in the table if it was passed
        if (this.editingItem) {
          this.editingItem.costPrice = res.costPrice;
          this.editingItem.mrp = res.mrp;
          this.editingItem.sellingPrice = res.sellingPrice;

          this.onQuantityChange(this.editingItem);
        }

        const fromAddBar = !this.editingItem;
        this.isPricingFormVisible = false;
        this.editingItem = null;

        if (addLine && fromAddBar) this.confirmAddToTable();
      },
      error: (err) => {
        this.toastr.error('Failed to add new pricing.', 'Error');
        console.error('Error adding pricing:', err);
      },
    });
  }

}

